import _ from "lodash";
import update from "immutability-helper";
import ACLStore from "lib/aclStore";
import client from "lib/apolloClient/client";
import { cache } from "lib/apolloClient";
import { i18n } from "lib/i18n";

import {
  getSelectedAcademicYearId,
  getCurrentCurriculumProgramType,
  getSelectedCourseId,
} from "modules/CommonSelectors";
import { getCreateAssessmentInputData } from "Assessments/modules/AssessmentModule";
import { getCourseDetailsFromCache } from "Course/modules/CourseGraphqlHelpers";
import {
  reorderEntityFlowResourceMutation,
  createEntityFlowResourceMutation,
  updateEntityFlowResourceMutation,
  deleteEntityFlowResourceMutation,
  repeatEntityFlowResourceMutation,
  importEntityFlowResourceMutation,
  createUnitResourceMutation,
  updateLockedStateMutation,
  shareEntityFlowResourceMutation,
} from "./Mutation";
import {
  dispatchError,
  generateRandomId,
  getCurriculumProgramIdFromState,
} from "Utils";
import {
  getNestedChildren,
  isFolder,
  isAssessment,
  getItemTypeLocale,
  isModule,
  getAncestors,
} from "Utils/EntityFlowUtils";
import { getIsUniversalParentEnabled } from "Utils/ACLStoreUtils";
import {
  getEntireUnitFlowFeedQuery,
  getUnitFlowOutlineFeedQuery,
  getUnitResourcesFeedQuery,
  getUnitFlowItemCountInUnitPlanQuery,
} from "./Query";
import {
  getEntireUnitFlowFeedQueryFromCache,
  getUnitFlowItemFromCache,
  getUnitResourcesFeedQueryFromCache,
  writeUnitFlowItemInCache,
  getLearningCourseFlowResourceFromCache,
} from "./GraphqlHelper";
import {
  getToastMsgForResourceUpdate,
  getUnitFlowFeedVariablesMemoized,
  getUnitPlanOutlineFeedVariablesMemoized,
  getUnitResourcesVariablesMemoized,
  isUnitFlowRevampEnabled,
  getUnitFlowFeedEdges,
  getWrapperFieldName,
  SHARE_ACTIONS,
  SHARING_STATES,
  getIsResourceSharedRecursivelyWithSameSettings,
} from "./utils";

import { CURRICULUM_TYPE_DP } from "Constants/stringConstants";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";
import { ASSESSMENT_TYPE_PARENT_GROUP_MAPPING } from "Constants";
import { MAX_DEPTH } from "UIComponents/NestedAccordion/constants";

import {
  setActiveContentData,
  setIsActionLoading,
  setMoveToSectionModalData,
  initialState,
} from "../CommonModules";

import { getUnitCombinedLibraryQuery } from "CombinedLibrary/modules/LearningCourseFlow/Query";
import {
  convertToCombinedLibraryResource,
  getCombinedLibraryFeedVariablesMemoized,
} from "CombinedLibrary/modules/LearningCourseFlow/utils";
import { getSelectedLearningCourseId } from "Teacher/routes/LearningCourse/modules/Utils";
import {
  GENERIC_ITEM_TYPES,
  getIsItemLocked,
  LOCKING_ACTIONS,
  LOCKING_SCOPE,
  getHasConflict,
} from "CourseFlow/modules/Utils";
import {
  getUnitPlanDetailsFromCache,
  writeUnitPlanFragment,
} from "IBPlanner/modules/IBGraphqlHelpers";
import { getUnitPlanFieldResponseFilter } from "Teacher/routes/Courses/routes/Course/routes/UnitPlans/modules/Utils";
import { getIsGradingPeriodUnlocked } from "GradingSetup/modules/Utils";

const getIsBlueprintSyncHistoryV2Enabled = () =>
  ACLStore.can("FeatureFlag:BPSyncHistoryV2");

// function to update childrenHierarchyInfo of all ancestors (bottom to top) of a resource
export const updateAncestorChildCountRecursively = ({ items, id, factor }) => {
  const item = _.find(items, ["node.id", id]);
  if (!item) return;

  const { node } = item;

  if (node?.childrenHierarchyInfo) {
    node.childrenHierarchyInfo.totalCount += factor;
  }

  if (node?.parent) {
    updateAncestorChildCountRecursively({
      items,
      id: node.parent.id,
      factor,
    });
  }
};

// updates depth recursively
const updateDepthRecursively = ({ items, id, factor }) => {
  const item = _.find(items, ["node.id", id]);
  if (!item) return;

  const { node } = item;
  node.depth += factor;

  const children = _.get(node, "childrenV2", []);

  _.forEach(children, childNode => {
    updateDepthRecursively({
      items,
      id: childNode.id,
      factor,
    });
  });
};

// recalculates max depth of all ancestors (bottom to top) of a resource
// this is required for drag and drop restrictions
export const recalculateMaxDepth = ({ items, id }) => {
  const item = _.find(items, ["node.id", id]);
  if (!item) return;

  const children = _.get(item, "node.childrenV2", []);
  const childrenItems = _.chain(children)
    .map(child => {
      return _.find(items, ["node.id", child.id]);
    })
    .filter(child => Boolean(child))
    .value();

  const maxDepth =
    children.length == 0
      ? 1
      : _.reduce(
          childrenItems,
          (result, { node }) => {
            return Math.max(
              result,
              _.get(node, "childrenHierarchyInfo.maxDepth", 1) + 1
            );
          },
          0
        );

  if (item?.node?.childrenHierarchyInfo) {
    item.node.childrenHierarchyInfo.maxDepth = maxDepth;
  }

  const parent = _.find(items, ["node.id", item.node.parent?.id]);
  if (parent) {
    recalculateMaxDepth({ items, id: parent.node.id });
  }
};

// when dragging an item after a folder, the putAfterItemId needs to be changed
// because we might need to put the new item after the last child of the folder
const interpolatePutAfterId = ({
  items: _items,
  id,
  putAfterItemId: _putAfterItemId,
  depth,
}) => {
  // remove current item from items
  const items = _.filter(_items, item => item.node.id !== id);
  const itemIndex = _.findIndex(items, ["node.id", _putAfterItemId]);
  const item = items[itemIndex];

  if (!item) return _putAfterItemId;

  let putAfterItemId = _putAfterItemId;

  const isFolder = item.node.resourceType === "FOLDER";
  if (isFolder) {
    const folderDepth = item.node.depth;
    if (depth > folderDepth) {
      putAfterItemId = _putAfterItemId;
    } else {
      let i = itemIndex;
      while (i < items.length) {
        i++;

        const nextItem = items[i];

        const itemDepth = _.get(nextItem, "node.depth");

        if (itemDepth <= depth) {
          const prevItem = items[i - 1];
          const itemId = prevItem?.node?.id;

          putAfterItemId = itemId ?? putAfterItemId;
          break;
        }

        // reached the end, put after the last item
        if (i === items.length - 1) {
          putAfterItemId = nextItem?.node?.id;
          break;
        }
      }
    }
  }

  return putAfterItemId;
};

/**
 * The data in main feed is different than their
 * child nodes, so pick only what is needed
 */
const getChildDataForCache = ({ node }) => {
  // return _.pick(node, ["id", "label", "resourceType", "displaySequence"]);
  return {
    id: node?.id,
  };
};

const removeRepeatedResourcesFromSourceFragment = ({
  itemId,
  variables,
  rollbackResources,
  itemIdsToDelete,
}) => {
  const cachedData = getEntireUnitFlowFeedQueryFromCache(variables);
  const cachedUnitFlowFeed = _.get(
    cachedData,
    "node.learningCourseFlowFeedWrapper.learningCourseFlowFeed",
    {}
  );

  // this is the source edge from entire feed cache
  const cachedSourceResourceEdge = findSourceResource({
    unitFlowEdges: cachedUnitFlowFeed.edges,
    itemId,
  });

  // this is the source resource from multinode cache which contains item(attachment/assessment) also
  const cachedSourceResource = getUnitFlowItemFromCache({
    id: _.get(cachedSourceResourceEdge, "node.id", ""),
    variables,
  });

  if (cachedSourceResource) {
    // used to rollback on error
    rollbackResources.push(cachedSourceResource);

    const sourceResource = _.cloneDeep(cachedSourceResource);

    // updating the repeated resources of the source resource
    sourceResource.repeatedResources = _.difference(
      sourceResource.repeatedResources,
      itemIdsToDelete
    );

    // updates the source resource in multinode cache
    writeUnitFlowItemInCache({
      id: sourceResource.id,
      variables,
      data: sourceResource,
    });
  }
};

// will be used to rollback the resources in cache in case of error
export const rollbackResourcesInCache = ({ rollbackResources }) => {
  _.forEach(rollbackResources, resource => {
    writeUnitFlowItemInCache({
      id: resource.id,
      variables: resource.variables,
      data: resource,
    });
  });
};

const getResourceCreationToast = ({
  isCreatingResource = false,
  totalResources = 0,
  isDraggedFromRightPane = false,
  isUploaded = false,
}) => {
  return setToastMsg({
    msg: isCreatingResource
      ? isDraggedFromRightPane
        ? "toastMsgs:action_to_unit_flow"
        : "toastMsgs:action_successfully"
      : "classMaterials:toast_msg_for_section_added",
    locale_params: [
      {
        key: "label",
        value:
          totalResources > 1
            ? "common:resource_label_plural"
            : "common:resource_label",
        isPlainText: false,
      },
      {
        key: "action",
        value:
          isDraggedFromRightPane || !isUploaded
            ? "toastMsgs:added"
            : "toastMsgs:uploaded",
        isPlainText: false,
      },
      {
        key: "count",
        value: totalResources,
        isPlainText: true,
      },
    ],
    type: "success",
    position: "toast-bottom-left",
  });
};

const getUnitPlanQueryVariables = ({ unitPlanId }) => {
  return async (dispatch, getState) => {
    const state = getState();
    const academicYearId = getSelectedAcademicYearId(state);
    const centralRepoInput = {
      isSchoolGroup: false,
      region: [],
    };

    const responseFilter = getUnitPlanFieldResponseFilter();

    const collaboratorsFilter = {
      userAcademicYearId: academicYearId,
    };

    return {
      id: unitPlanId,
      unitPlanId,
      centralRepoInput,
      responseFilter,
      collaboratorsFilter,
      exceptionalCollaboratorsFilter: collaboratorsFilter,
      skipTitle: ACLStore.can("FeatureFlag:RemoveCourseDetailsFromStaffList"),
    };
  };
};

export const updateUnitFlowItemCountInUnitPlan = ({
  unitPlanId,
  incrementBy = 0,
  decrementBy = 0,
}) => {
  return async (dispatch, getState) => {
    const unitPlanQueryVariables = await dispatch(
      getUnitPlanQueryVariables({
        unitPlanId,
      })
    );

    const unitPlanDetails = getUnitPlanDetailsFromCache(unitPlanQueryVariables);
    const itemCount = _.get(unitPlanDetails, "flowResourceCount.totalCount", 0);

    const updatedCount = itemCount + incrementBy - decrementBy;

    // Update the unit flow item count in unit plan details
    const updatedUnitPlanDetails = update(unitPlanDetails, {
      flowResourceCount: {
        totalCount: { $set: updatedCount },
      },
    });

    // Write the updated unit plan details in cache
    writeUnitPlanFragment({
      unitPlanId,
      data: updatedUnitPlanDetails,
      ...unitPlanQueryVariables,
    });
  };
};

const getAssessmentCreationToast = ({ isDraggedFromRightPane = false }) =>
  setToastMsg({
    msg: isDraggedFromRightPane
      ? "toastMsgs:action_to_unit_flow"
      : "toastMsgs:action_successfully",
    locale_params: [
      {
        key: "label",
        value: "common:task",
        isPlainText: false,
      },
      {
        key: "action",
        value: isDraggedFromRightPane ? "toastMsgs:added" : "toastMsgs:created",
        isPlainText: false,
      },
    ],
    type: "success",
    position: "toast-bottom-left",
  });

export const reorderUnitFlowResources = ({
  id,
  putAfterItemId,
  parentId,
  depth,
  isMovedByDragAndDrop,
}) => {
  return async (dispatch, getState) => {
    if (id == putAfterItemId || id == parentId) {
      // Dropping item to it's original position
      return;
    }
    const state = getState();
    const userInfo = getUserInfo({ portalType: "PLANNER" });
    const curriculumType = _.get(
      state,
      "platform.currentCurriculumProgram.type",
      null
    );
    const unitPlanId = _.get(state, "planner.unitPlanData.unitPlanId", "");
    const filters = _.get(state, "unitFlow.filters", {});
    const learningCourseId = _.get(
      state,
      "planner.unitPlanData.learningCourseId",
      null
    );
    const feedQueryVariables = getUnitFlowFeedVariablesMemoized({
      unitPlanId,
      filters,
      userInfo,
      curriculumType,
      learningCourseId,
    });

    let switchParent;
    let resourceType;

    try {
      cache.updateQuery(
        {
          query: getEntireUnitFlowFeedQuery,
          variables: feedQueryVariables,
        },
        data => {
          if (!data) return;

          const unitFlowFeed = _.cloneDeep(
            _.get(
              data,
              "node.learningCourseFlowFeedWrapper.learningCourseFlowFeed",
              {}
            )
          );

          const unitFlowFeedNodes = _.map(unitFlowFeed.edges, "node");

          const item = _.find(unitFlowFeed.edges, ({ node }) => node.id === id);
          if (!item) return;

          resourceType = _.get(item, "node.resourceType", "");

          // modify putAfterItemId to reflect the correct position in case of folders
          const _putAfterItemId = interpolatePutAfterId({
            id,
            putAfterItemId,
            items: unitFlowFeed.edges,
            depth,
          });

          // remove the item from its current position and place after putAfterItem
          const newEdges = _.reduce(
            unitFlowFeed.edges,
            (result, edge, i) => {
              // item was dragged and dropped to the first position
              if (i === 0 && !_putAfterItemId) {
                result.push(item);
                result.push(edge);
                return result;
              }

              if (edge.node.id === id) return result;

              // put it after putAfterItem
              if (edge.node.id === _putAfterItemId) {
                result.push(edge);
                result.push(item);
              } else {
                result.push(edge);
              }
              return result;
            },
            []
          );

          unitFlowFeed.edges = newEdges;

          let lastItemId = item.node.id;

          // also move children after the item
          const nestedChildren = getNestedChildren({
            items: _.map(unitFlowFeed.edges, "node"),
            id,
          });

          _.forEach(nestedChildren, child => {
            const childIndex = _.findIndex(
              unitFlowFeed.edges,
              ({ node }) => node.id === child.id
            );

            if (childIndex !== -1) {
              const childItem = _.first(
                unitFlowFeed.edges.splice(childIndex, 1)
              );

              const indexToMove = _.findIndex(unitFlowFeed.edges, {
                node: { id: lastItemId },
              });

              unitFlowFeed.edges.splice(indexToMove + 1, 0, childItem);

              lastItemId = child.id;
            }
          });

          const currentParentId = _.get(item, "node.parent.id");
          switchParent = parentId !== currentParentId;

          // adding this condition for null and undefined comparisons
          if (_.isEmpty(currentParentId) && _.isEmpty(parentId)) {
            switchParent = false;
          }

          // remove from current parent's children and put in new parent's children
          if (switchParent) {
            const currentParent = _.find(unitFlowFeedNodes, {
              id: currentParentId,
            });

            if (currentParent) {
              currentParent.childrenV2 = _.filter(
                currentParent.childrenV2,
                ({ id }) => id !== id
              );
              updateAncestorChildCountRecursively({
                id: currentParent.id,
                items: unitFlowFeed.edges,
                factor: -(
                  (isFolder(item.node) ? 0 : 1) +
                  _.get(item, "node.childrenHierarchyInfo.totalCount", 0)
                ),
              });

              recalculateMaxDepth({
                items: unitFlowFeed.edges,
                id: currentParent.id,
              });
            }

            if (parentId) {
              item.node.parent = {
                ...(item.node.parent ?? {}),
                id: parentId,
              };

              const parent = _.find(unitFlowFeedNodes, { id: parentId });
              if (parent) {
                parent.childrenV2.push(_.get(item, "node"));
                updateAncestorChildCountRecursively({
                  id: parentId,
                  items: unitFlowFeed.edges,
                  factor:
                    (isFolder(item.node) ? 0 : 1) +
                    _.get(item, "node.childrenHierarchyInfo.totalCount", 0),
                });
              }
            } else item.node.parent = null;
          }

          const currentDepth = _.get(item, "node.depth", 1);
          const factor = depth - currentDepth;

          updateDepthRecursively({
            items: unitFlowFeed.edges,
            id: item.node.id,
            factor,
          });

          recalculateMaxDepth({
            items: unitFlowFeed.edges,
            id: item.node.id,
          });

          return update(data, {
            node: {
              learningCourseFlowFeedWrapper: {
                learningCourseFlowFeed: { $set: unitFlowFeed },
              },
            },
          });
        }
      );

      dispatch(setMoveToSectionModalData({}));

      const outlineQueryVariables = getUnitPlanOutlineFeedVariablesMemoized({
        unitPlanId,
        filters,
        userInfo,
        curriculumType,
        learningCourseId,
      });

      let _parentId = parentId;
      if (!_parentId) {
        const learningCourseFlowResource = getLearningCourseFlowResourceFromCache(
          { id: unitPlanId }
        );
        _parentId = _.get(
          learningCourseFlowResource,
          "node.learningCourseFlowResource.id",
          null
        );
      }

      await client.mutate({
        mutation: reorderEntityFlowResourceMutation,
        variables: {
          input: {
            id,
            putAfterItemId,
            parentId: _parentId,
            switchParent,
          },
        },

        refetchQueries: [
          {
            query: getUnitFlowOutlineFeedQuery,
            variables: outlineQueryVariables,
          },
        ],
      });

      if (!isMovedByDragAndDrop) {
        let title;
        switch (resourceType) {
          case "FOLDER":
            title = "common:folder";
            break;
          case "ASSIGNMENT":
          case "ASSESSMENT": {
            title = "toastMsgs:assignment";
            break;
          }
          default:
            title = `toastMsgs:resource`;
        }

        dispatch(
          setToastMsg({
            type: "success",
            msg: "toastMsgs:label_successfully_moved",
            locale_params: [
              {
                key: "label",
                value: title,
                isPlainText: false,
              },
            ],
          })
        );
      }
    } catch (error) {
      console.error(error);
      dispatchError({ dispatch, e: error });
    }
  };
};

const findSourceResource = ({ unitFlowEdges, itemId }) => {
  return _.find(unitFlowEdges, edge => {
    const { node } = edge;
    return node.isRepeated === false && _.get(node, "item.id") === itemId;
  });
};

export const createUnitResource = ({ resources }) => {
  return async (dispatch, getState) => {
    const state = getState();

    const unitPlanId = _.get(state, "planner.unitPlanData.unitPlanId", "");
    const searchText = _.get(
      state,
      "organizationResource.filters.searchText",
      ""
    );

    const learningCourseId = getSelectedLearningCourseId(state);
    const classId = getSelectedCourseId(state);

    const feedQueryVariables = getUnitResourcesVariablesMemoized({
      parentId: unitPlanId,
      learningCourseId,
      classId,
      searchText,
    });

    const cachedData = getUnitResourcesFeedQueryFromCache(feedQueryVariables);
    const unitResourceId = _.get(
      cachedData,
      "node.learningCourseFlowFeedWrapper.learningCourseFlowFeed",
      ""
    );

    try {
      const variables = {
        input: {
          parentId: null,
          putAfterItemId: null,
          entityFlowId: unitResourceId,
          resources,
        },
      };

      await client.mutate({
        mutation: createUnitResourceMutation,
        variables,
        update: (
          cache,
          {
            data: {
              planner: { createLearningCourseFlowResource },
            },
          }
        ) => {
          cache.updateQuery(
            {
              query: getUnitResourcesFeedQuery,
              variables: feedQueryVariables,
            },
            data => {
              if (!data) return;

              const unitResourcesEdges = _.cloneDeep(
                getUnitFlowFeedEdges(data)
              );

              const updatedEdges = [
                {
                  node: createLearningCourseFlowResource[0],
                  __typename: "LearningCourseFlowEdge",
                },
                ...unitResourcesEdges,
              ];
              return update(data, {
                node: {
                  learningCourseFlowFeedWrapper: {
                    learningCourseFlowFeed: {
                      edges: { $set: updatedEdges },
                    },
                  },
                },
              });
            }
          );
        },
      });
    } catch (error) {
      console.error(error);
      if (error.networkError) {
        dispatch(setToastMsg("toastMsgs:no_internet_connection"));
      } else {
        dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      }
    }
  };
};

export const updateUnitResources = ({ id, label, attachment }) => {
  return async (dispatch, getState) => {
    const state = getState();

    // variables for updating right pane resources
    const unitPlanId = _.get(state, "planner.unitPlanData.unitPlanId", "");
    const searchText = _.get(
      state,
      "organizationResource.filters.searchText",
      ""
    );

    const feedQueryVariables = getUnitResourcesVariablesMemoized({
      parentId: unitPlanId,
      searchText,
    });

    const variables = {
      input: {
        id,
        label,
        attachment,
      },
    };

    // variables for updating unit flow feed
    const filters = _.get(state, "unitFlow.filters", {});

    const userInfo = getUserInfo({ portalType: "PLANNER" });
    const curriculumType = _.get(
      state,
      "platform.currentCurriculumProgram.type",
      null
    );
    const learningCourseId = _.get(
      state,
      "planner.unitPlanData.learningCourseId",
      null
    );

    const unitFlowfeedQueryVariables = getUnitFlowFeedVariablesMemoized({
      unitPlanId,
      filters,
      userInfo,
      curriculumType,
      learningCourseId,
    });

    const rollbackResources = [];
    try {
      const unitFlowFeedData = getEntireUnitFlowFeedQueryFromCache(
        unitFlowfeedQueryVariables
      );
      const unitFlowFeed = _.cloneDeep(
        _.get(
          unitFlowFeedData,
          "node.learningCourseFlowFeedWrapper.learningCourseFlowFeed.edges",
          []
        )
      );
      _.forEach(unitFlowFeed, edge => {
        const { node } = edge;
        if (node.item?.id !== id) return;

        const cachedUnitFlowNode = getUnitFlowItemFromCache({
          id: node.id,
          variables: unitFlowfeedQueryVariables,
        });

        if (cachedUnitFlowNode) {
          // used to rollback on error
          rollbackResources.push(cachedUnitFlowNode);

          const unitFlowNode = _.cloneDeep(cachedUnitFlowNode);

          unitFlowNode.label = label;

          if (unitFlowNode?.item?.attachment) {
            const nodeAttachment = unitFlowNode.item.attachment;
            nodeAttachment.name = label;
            if (nodeAttachment.attachmentType === "LINK") {
              nodeAttachment.url = attachment.url;
              nodeAttachment.thumbUrl = attachment.thumbUrl;
            } else if (nodeAttachment.attachmentType === "NOTE") {
              nodeAttachment.name = label;
              nodeAttachment.attachmentTitle = label;
              nodeAttachment.title = label;
            } else {
              nodeAttachment.name = label;
            }
          }

          writeUnitFlowItemInCache({
            id: node.id,
            variables: unitFlowfeedQueryVariables,
            data: unitFlowNode,
          });
        }
      });

      await client.mutate({
        mutation: updateEntityFlowResourceMutation,
        variables,
        optimisticResponse: {
          __typename: "Mutation",
          planner: {
            updateLearningCourseFlowResource: {
              id,
              __typename: "LearningCourseFlow",
            },
            __typename: "PlannerMutations",
          },
        },
        update: cache => {
          cache.updateQuery(
            {
              query: getUnitResourcesFeedQuery,
              variables: feedQueryVariables,
            },
            data => {
              if (!data) return;

              const unitResources = _.cloneDeep(
                _.get(
                  data,
                  "node.learningCourseFlowFeedWrapper.learningCourseFlowFeed.edges",
                  {}
                )
              );
              const itemIdx = _.findIndex(
                unitResources,
                ({ node }) => node.id === id
              );

              const newItem = _.cloneDeep(unitResources[itemIdx]);

              if (newItem) {
                newItem.node.label = label;
                newItem.node.attachment = {
                  ...newItem.node.attachment,
                  ...attachment,
                };

                return update(data, {
                  node: {
                    learningCourseFlowFeedWrapper: {
                      learningCourseFlowFeed: {
                        edges: {
                          [itemIdx]: { $set: newItem },
                        },
                      },
                    },
                  },
                });
              }
            }
          );

          cache.updateQuery(
            {
              query: getEntireUnitFlowFeedQuery,
              variables: unitFlowfeedQueryVariables,
            },
            data => {
              if (!data) return;

              const updatedEdges = _.map(unitFlowFeed, edge => {
                const { node } = edge;
                if (node.item?.id === id) node.label = label;
                return edge;
              });

              return update(data, {
                node: {
                  learningCourseFlowFeedWrapper: {
                    learningCourseFlowFeed: {
                      edges: { $set: updatedEdges },
                    },
                  },
                },
              });
            }
          );
        },
      });
      dispatch(setToastMsg(getToastMsgForResourceUpdate("FILE")));
    } catch (error) {
      rollbackResourcesInCache({ rollbackResources });

      console.error(error);
      if (error.networkError) {
        dispatch(setToastMsg("toastMsgs:no_internet_connection"));
      } else {
        dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      }
    }
  };
};

export const deleteUnitResources = ({ ids }) => {
  return async (dispatch, getState) => {
    const state = getState();
    const unitPlanId = _.get(state, "planner.unitPlanData.unitPlanId", "");
    const searchText = _.get(
      state,
      "organizationResource.filters.searchText",
      ""
    );

    const classId = getSelectedCourseId(state);

    const feedQueryVariables = getUnitResourcesVariablesMemoized({
      parentId: unitPlanId,
      learningCourseId: getSelectedLearningCourseId(state),
      classId,
      searchText,
    });

    // variables for updating unit flow feed
    const filters = _.get(state, "unitFlow.filters", {});

    const userInfo = getUserInfo({ portalType: "PLANNER" });
    const curriculumType = _.get(
      state,
      "platform.currentCurriculumProgram.type",
      null
    );
    const learningCourseId = _.get(
      state,
      "planner.unitPlanData.learningCourseId",
      null
    );

    const unitFlowfeedQueryVariables = getUnitFlowFeedVariablesMemoized({
      unitPlanId,
      filters,
      userInfo,
      curriculumType,
      learningCourseId,
    });
    let countOfDeletedItems = 0;

    try {
      await client.mutate({
        mutation: deleteEntityFlowResourceMutation,
        variables: { input: { ids } },
        optimisticResponse: {
          __typename: "Mutation",
          planner: {
            deleteLearningCourseFlowResource: true,
            __typename: "PlannerMutations",
          },
        },
        update: cache => {
          cache.updateQuery(
            {
              query: getUnitResourcesFeedQuery,
              variables: feedQueryVariables,
            },
            data => {
              if (!data) return;

              const unitResources = _.cloneDeep(
                _.get(
                  data,
                  "node.learningCourseFlowFeedWrapper.learningCourseFlowFeed.edges",
                  {}
                )
              );

              const updatedEdges = _.filter(
                unitResources,
                ({ node }) => !_.includes(ids, node.id)
              );

              return update(data, {
                node: {
                  learningCourseFlowFeedWrapper: {
                    learningCourseFlowFeed: {
                      edges: { $set: updatedEdges },
                    },
                  },
                },
              });
            }
          );

          cache.updateQuery(
            {
              query: getEntireUnitFlowFeedQuery,
              variables: unitFlowfeedQueryVariables,
            },
            data => {
              if (!data) return;

              const unitFlowFeed = _.cloneDeep(
                _.get(
                  data,
                  "node.learningCourseFlowFeedWrapper.learningCourseFlowFeed"
                )
              );

              const unitFlowFeedNodes = _.map(unitFlowFeed.edges, "node");
              const itemIdsToDelete = _.map(
                _.filter(unitFlowFeed?.edges, ({ node }) => {
                  const { item } = node;
                  return item && _.includes(ids, item?.id);
                }),
                ({ node }) => node.id
              );

              const items = _.filter(unitFlowFeedNodes, ({ id }) =>
                _.includes(itemIdsToDelete, id)
              );

              // update children and count of parents
              _.forEach(items, item => {
                const parent = _.find(
                  unitFlowFeedNodes,
                  ({ id }) => id === item.parent?.id
                );

                if (parent) {
                  parent.childrenV2 = _.filter(
                    parent.childrenV2,
                    ({ id }) => id !== item.id
                  );

                  updateAncestorChildCountRecursively({
                    id: parent.id,
                    items: unitFlowFeed.edges,
                    factor: isFolder(item) ? 0 : -1,
                  });

                  recalculateMaxDepth({
                    items: unitFlowFeed.edges,
                    id: parent.id,
                  });
                }
              });

              countOfDeletedItems = _.reduce(
                items,
                (result, item) => {
                  result += _.get(item, "childrenHierarchyInfo.totalCount", 0);
                  result += isFolder(item) ? 0 : 1;
                  return result;
                },
                0
              );

              const updatedFeed = {
                ...unitFlowFeed,
                edges: _.filter(
                  unitFlowFeed.edges,
                  ({ node }) => !_.includes(itemIdsToDelete, node.id)
                ),
                resourceTypeBasedCount: {
                  ...unitFlowFeed?.resourceTypeBasedCount,
                  totalCount:
                    _.get(
                      unitFlowFeed,
                      "resourceTypeBasedCount.totalCount",
                      0
                    ) - countOfDeletedItems,
                },
              };

              return update(data, {
                node: {
                  learningCourseFlowFeedWrapper: {
                    learningCourseFlowFeed: { $set: updatedFeed },
                  },
                },
              });
            }
          );
        },
      });
    } catch (error) {
      console.error(error);
      if (error.networkError) {
        dispatch(setToastMsg("toastMsgs:no_internet_connection"));
      } else {
        dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      }
    }
  };
};

export const createUnitFlowResources = ({
  resources,
  putAfterItemId: _putAfterItemId = null,
  setNewDataIds,
  isFolderCollapsed: _isFolderCollapsed = false,
  quickCreation = false,
  sectionCreationType,
  showLoader = false,
  parentId,
  newDepth: _newDepth,
  isAssessmentImport = false,
  showToast = true,
  shouldAddToBottom = false,
  isImportFromUnit = false,
  rightPaneItem,
}) => {
  return async (dispatch, getState) => {
    if (_.isEmpty(resources)) return;
    const state = getState();

    const defaultPositioningOfNewItems = _.get(
      state,
      "unitFlow.displaySettings.add_to_top",
      false
    );

    const _addToTop = shouldAddToBottom ? false : defaultPositioningOfNewItems;

    const addToTop =
      _putAfterItemId === null && (Boolean(rightPaneItem) || _addToTop);

    const learningCourseId = _.get(
      state,
      "planner.unitPlanData.learningCourseId",
      null
    );
    const combinedLibraryFilters = _.get(
      state,
      "unitPlans.combinedLibrary.filters",
      {}
    );
    const curriculumType = _.get(
      state,
      "platform.currentCurriculumProgram.type",
      null
    );
    const unitPlanId = _.get(state, "planner.unitPlanData.unitPlanId", "");

    // variables for updating right pane
    const combinedLibraryFeedQueryVariables = getCombinedLibraryFeedVariablesMemoized(
      {
        unitPlanId,
        filters: combinedLibraryFilters,
        learningCourseId,
      }
    );

    const isInlineCreateOrEdit = _.get(
      state,
      "unitFlow.activeContentData.isInlineCreateOrEditItem",
      false
    );
    const isCreatingAssessment = _.some(resources, {
      itemType: "ASSESSMENT",
    });

    const isDraggedFromRightPane = !_.isEmpty(rightPaneItem);
    const resource = _.first(resources);

    const filters = _.get(state, "unitFlow.filters", {});

    const userInfo = getUserInfo({ portalType: "PLANNER" });

    const feedQueryVariables = getUnitFlowFeedVariablesMemoized({
      unitPlanId,
      filters,
      userInfo,
      curriculumType,
      learningCourseId,
    });

    if (showLoader) {
      dispatch(setIsActionLoading(true));
    }

    try {
      const cachedData = getEntireUnitFlowFeedQueryFromCache(
        feedQueryVariables
      );

      const unitFlowFeed = _.cloneDeep(
        _.get(
          cachedData,
          "node.learningCourseFlowFeedWrapper.learningCourseFlowFeed",
          {}
        )
      );

      let putAfterItemId = _putAfterItemId,
        isFolderCollapsed = _isFolderCollapsed;

      if (isCreatingAssessment) {
        const isInlineCreation = _.get(
          state,
          "unitFlow.activeContentData.isInlineCreateOrEditItem",
          false
        );

        let itemState;
        switch (true) {
          case isInlineCreation:
            itemState = "activeContentData";
            break;
          case isAssessmentImport:
            itemState = "assessmentImportModalData";
            break;
          case isImportFromUnit:
            itemState = "assessmentSelectModalData";
            break;
          default:
            itemState = "assignmentModalData";
        }

        putAfterItemId = _.get(
          state,
          `unitFlow.${itemState}.putAfterItemId`,
          putAfterItemId
        );

        isFolderCollapsed = _.get(
          state,
          `unitFlow.${itemState}.isFolderCollapsed`,
          isFolderCollapsed
        );
      }

      const putAfterItem = _.find(
        unitFlowFeed?.edges,
        ({ node }) => node.id === putAfterItemId
      )?.node;

      const isSection = isFolder(putAfterItem);

      // when creating a section in parallel to current
      const isParallelSection = sectionCreationType === "PARALLEL";

      // when creating a subsection to the current section which was previously collapsed
      const isExpandedSubsection =
        sectionCreationType === "EXPANDED_SUBSECTION";

      // if putAfterItem is a section, then newly created resources are meant to go inside it
      // otherwise, we find the parent of putAfterItem
      let parent = putAfterItem;

      const isUniversalParentEnabled = getIsUniversalParentEnabled();
      const putAfterItemDepth = putAfterItem?.depth ?? 0;
      const isUniversalParentEligible =
        isUniversalParentEnabled &&
        !isSection &&
        (_newDepth !== undefined
          ? _newDepth > putAfterItemDepth
          : putAfterItemDepth < MAX_DEPTH);

      // if putAfterItem is collapsed, new item should be created after it (as sibling)
      const shouldPutInsideSection =
        (isSection || isUniversalParentEligible) &&
        !isFolderCollapsed &&
        !isParallelSection;

      if (!shouldPutInsideSection) {
        const newParentId =
          isParallelSection || (isExpandedSubsection && parentId)
            ? parentId
            : putAfterItem?.parent?.id;

        parent = _.find(
          unitFlowFeed?.edges,
          ({ node }) => node.id === newParentId
        )?.node;
      }

      const parentResourceId = shouldPutInsideSection
        ? putAfterItemId
        : parent?.id;

      const outlineQueryVariables = getUnitPlanOutlineFeedVariablesMemoized({
        unitPlanId,
        filters,
        userInfo,
        curriculumType,
        learningCourseId,
      });

      let _parentId = parentResourceId || null;
      if (!_parentId) {
        const learningCourseFlowResource = getLearningCourseFlowResourceFromCache(
          { id: unitPlanId }
        );
        _parentId = _.get(
          learningCourseFlowResource,
          "node.learningCourseFlowResource.id",
          null
        );
      }

      const variables = {
        input: {
          learningCourseId,
          parentId: _parentId,
          putAfterItemId,
          resources,
          addToTop,
        },
        filters: _.get(feedQueryVariables, "filters", {}),
        syncedBluePrintItemFilters: {
          learningCourseId,
        },
      };

      const totalCreatedItems = _.size(resources);

      dispatch(
        updateUnitFlowItemCountInUnitPlan({
          unitPlanId,
          incrementBy: totalCreatedItems,
        })
      );

      return await client.mutate({
        mutation: createEntityFlowResourceMutation,
        variables,
        awaitRefetchQueries: true,
        refetchQueries: [
          {
            query: getUnitFlowOutlineFeedQuery,
            variables: outlineQueryVariables,
          },
        ],
        update: (
          cache,
          {
            data: {
              planner: { createLearningCourseFlowResource },
            },
          }
        ) => {
          if (
            _.isEmpty(unitFlowFeed) ||
            _.isEmpty(createLearningCourseFlowResource)
          )
            return;

          let newDepth = 1;
          if (putAfterItem) {
            newDepth = putAfterItem.depth;
            if (shouldPutInsideSection) newDepth += 1;
          }

          if (isParallelSection && parent.depth) {
            newDepth = parent.depth + 1;
          }

          newDepth = _newDepth ?? newDepth;

          cache.updateQuery(
            {
              query: getEntireUnitFlowFeedQuery,
              variables: feedQueryVariables,
            },
            data => {
              if (!data) return;

              const createdResources = _.map(
                _.cloneDeep(createLearningCourseFlowResource),
                resource => ({
                  __typename: "LearningCourseFlowEdge",
                  node: {
                    __typename: "LearningCourseFlow",
                    ...resource,
                    ...(isFolder(resource) &&
                    _.isEmpty(resource.childrenHierarchyInfo)
                      ? {
                          childrenHierarchyInfo: {
                            maxDepth: 1,
                            totalCount: 0,
                            totalCountWithFolder: 0,
                            __typename: "LearningCourseFlowChildHierarchyInfo",
                          },
                        }
                      : {}),
                    depth: resource.depth ?? newDepth,
                  },
                })
              );

              if (putAfterItemId && parent) {
                const createdResourcesAsChild = _.map(
                  createdResources,
                  resource => {
                    const { node } = resource;

                    return {
                      __typename: "LearningCourseFlow",
                      ...getChildDataForCache({ node }),
                    };
                  }
                );

                parent.childrenV2 = [
                  ...createdResourcesAsChild,
                  ...parent.childrenV2,
                ];

                const factor = _.reduce(
                  createdResources,
                  (acc, resource) => acc + (isFolder(resource.node) ? 0 : 1),
                  0
                );

                updateAncestorChildCountRecursively({
                  id: parent.id,
                  items: unitFlowFeed.edges,
                  factor,
                });
              }
              const hasNextPage = _.get(unitFlowFeed, "pageInfo.hasNextPage");

              // if put after item is present, then add in edges after the id
              if (putAfterItemId) {
                let index = _.findIndex(
                  unitFlowFeed.edges,
                  ({ node }) => node.id === putAfterItemId
                );

                if (
                  (isSection || isUniversalParentEligible) &&
                  (isFolderCollapsed || putAfterItem.depth >= newDepth)
                ) {
                  const feedLength = unitFlowFeed.edges.length;

                  for (let i = index + 1; i < feedLength; i++) {
                    const item = unitFlowFeed.edges[i];
                    if (item.node.depth <= newDepth) {
                      index = i - 1;
                      break;
                    }

                    // edge case: this is the last folder in the feed
                    if (i === feedLength - 1) {
                      index = i;
                      break;
                    }
                  }
                }

                unitFlowFeed.edges.splice(index + 1, 0, ...createdResources);
              } else {
                // add to the top if it is dragged from the rightPane to the first position
                // otherwise add to the last position
                if (!hasNextPage) {
                  const edges = unitFlowFeed.edges;
                  unitFlowFeed.edges = addToTop
                    ? [...createdResources, ...edges]
                    : [...edges, ...createdResources];
                }
              }

              if (parent) {
                recalculateMaxDepth({
                  items: unitFlowFeed.edges,
                  id: parent.id,
                });
              }

              // update source resource if we are repeating resources
              _.forEach(createdResources, resourceEdge => {
                const resource = _.get(resourceEdge, "node", {});
                const { id, isRepeated, item } = resource;

                if (isRepeated) {
                  const sourceResourceEdge = findSourceResource({
                    unitFlowEdges: unitFlowFeed.edges,
                    itemId: item.id,
                  });

                  if (sourceResourceEdge) {
                    sourceResourceEdge.node.repeatedResources.push(id);
                  }
                }
              });

              setNewDataIds?.(
                _.map(createLearningCourseFlowResource, "id", [])
              );

              return update(data, {
                node: {
                  learningCourseFlowFeedWrapper: {
                    learningCourseFlowFeed: {
                      edges: { $set: unitFlowFeed.edges },
                      resourceBasedCount: {
                        totalCount: {
                          $set:
                            _.get(
                              unitFlowFeed,
                              "resourceBasedCount.totalCount",
                              0
                            ) + (createdResources?.length || 0),
                        },
                      },
                    },
                  },
                },
              });
            }
          );

          if (!isFolder(_.first(createLearningCourseFlowResource))) {
            cache.updateQuery(
              {
                query: getUnitCombinedLibraryQuery,
                variables: combinedLibraryFeedQueryVariables,
              },
              data => {
                if (!data) return;
                const oldEdges = _.cloneDeep(
                  _.get(
                    data,
                    "node.learningCourseFlowFeedWrapper.learningCourseFlowFeed.edges",
                    []
                  )
                );
                let updatedEdges = [];
                // if the resource is added from rightPane no need to add again in rightPane
                if (rightPaneItem) return;
                const newEdges = _.map(
                  createLearningCourseFlowResource,
                  resource => ({
                    node: convertToCombinedLibraryResource({ resource }),
                    __typename: "LearningCourseFlowEdge",
                  })
                );
                updatedEdges = [...newEdges, ...oldEdges];
                return update(data, {
                  node: {
                    learningCourseFlowFeedWrapper: {
                      learningCourseFlowFeed: {
                        edges: { $set: updatedEdges },
                      },
                    },
                  },
                });
              }
            );
          }

          setTimeout(() => {
            if (isInlineCreateOrEdit) {
              dispatch(setActiveContentData({}));
            }

            if (!quickCreation && !isImportFromUnit && !isAssessmentImport) {
              if (isCreatingAssessment && showToast) {
                dispatch(
                  getAssessmentCreationToast({ isDraggedFromRightPane })
                );
              } else {
                const resourceInput = resource?.itemInput;
                const isCreatingResource =
                  _.has(resourceInput, "attachment") || isDraggedFromRightPane;
                const totalResources = _.size(resources);
                const isUploaded =
                  !isDraggedFromRightPane &&
                  _.has(resourceInput?.attachment, "metadata");

                dispatch(
                  getResourceCreationToast({
                    isCreatingResource,
                    totalResources,
                    isDraggedFromRightPane,
                    isUploaded,
                  })
                );
              }
            }
          });
        },
      });
    } catch (error) {
      console.error(error);
      dispatchError({ dispatch, e: error });
    } finally {
      if (isInlineCreateOrEdit) {
        dispatch(setActiveContentData({}));
      }
      dispatch(setIsActionLoading(false));
    }
  };
};

export const updateUnitFlowResources = ({
  id,
  label,
  attachment,
  resourceType,
}) => {
  return async (dispatch, getState) => {
    const state = getState();

    // variables for updating unit flow feed
    const unitPlanId = _.get(state, "planner.unitPlanData.unitPlanId", "");
    const filters = _.get(state, "unitFlow.filters", {});
    const learningCourseId = _.get(
      state,
      "planner.unitPlanData.learningCourseId",
      null
    );

    const isInlineCreateOrEdit = _.get(
      state,
      "unitFlow.activeContentData.isInlineCreateOrEditItem",
      false
    );

    const userInfo = getUserInfo({ portalType: "PLANNER" });
    const curriculumType = _.get(
      state,
      "platform.currentCurriculumProgram.type",
      null
    );

    const feedQueryVariables = getUnitFlowFeedVariablesMemoized({
      unitPlanId,
      filters,
      userInfo,
      curriculumType,
      learningCourseId,
    });

    const outlineQueryVariables = getUnitPlanOutlineFeedVariablesMemoized({
      unitPlanId,
      learningCourseId,
    });

    const isResource = _.includes(["NOTE", "LINK", "FILE"], resourceType);

    const variables = {
      input: {
        id,
        label,
      },
    };

    if (attachment) {
      if (_.includes(["LINK", "NOTE"], resourceType)) {
        variables.input.attachment = attachment;
      } else {
        variables.input.attachment = {
          type: _.get(attachment, "type", "FILE"),
          name: label,
        };
      }
    }

    // variables for updating right pane resources
    const combinedLibraryFilters = _.get(
      state,
      "unitPlans.combinedLibrary.filters",
      {}
    );

    const combinedLibraryFeedQueryVariables = getCombinedLibraryFeedVariablesMemoized(
      {
        unitPlanId,
        filters: combinedLibraryFilters,
        learningCourseId,
      }
    );

    const rollbackResources = [];
    try {
      const unitFlowFeedData = getEntireUnitFlowFeedQueryFromCache(
        feedQueryVariables
      );

      const unitFlowFeed = _.cloneDeep(
        _.get(
          unitFlowFeedData,
          "node.learningCourseFlowFeedWrapper.learningCourseFlowFeed.edges",
          []
        )
      );

      const originalNode = _.find(unitFlowFeed, ({ node }) => node.id === id);
      const itemId = _.get(originalNode, "node.item.id", "");

      if (isResource) {
        _.forEach(unitFlowFeed, edge => {
          const { node } = edge;
          if (node.item?.id !== itemId) return;

          const cachedUnitFlowNode = getUnitFlowItemFromCache({
            id: node.id,
            variables: feedQueryVariables,
          });

          if (cachedUnitFlowNode) {
            // used to rollback on error
            rollbackResources.push(cachedUnitFlowNode);

            const unitFlowNode = _.cloneDeep(cachedUnitFlowNode);

            unitFlowNode.label = label;

            if (unitFlowNode?.item?.attachmentType) {
              const nodeAttachment = unitFlowNode.item;
              nodeAttachment.name = label;
              if (nodeAttachment.attachmentType === "LINK") {
                nodeAttachment.url = attachment.url;
                nodeAttachment.thumbUrl = attachment.thumbUrl;
              } else if (nodeAttachment.attachmentType === "NOTE") {
                nodeAttachment.name = label;
                nodeAttachment.attachmentTitle = label;
                nodeAttachment.title = label;
              } else {
                nodeAttachment.name = label;
              }
            }

            writeUnitFlowItemInCache({
              id: node.id,
              variables: feedQueryVariables,
              data: unitFlowNode,
            });
          }
        });
      } else {
        const cachedUnitFlowNode = getUnitFlowItemFromCache({
          id,
          variables: feedQueryVariables,
        });

        if (cachedUnitFlowNode) {
          // used to rollback on error
          rollbackResources.push(cachedUnitFlowNode);

          const unitFlowNode = _.cloneDeep(cachedUnitFlowNode);

          unitFlowNode.label = label;

          writeUnitFlowItemInCache({
            id: id,
            variables: feedQueryVariables,
            data: unitFlowNode,
          });
        }
      }

      dispatch(setActiveContentData({}));

      await client.mutate({
        mutation: updateEntityFlowResourceMutation,
        variables,
        optimisticResponse: {
          __typename: "Mutation",
          planner: {
            updateLearningCourseFlowResource: {
              id,
              __typename: "LearningCourseFlow",
            },
            __typename: "PlannerMutations",
          },
        },
        update: cache => {
          cache.updateQuery(
            {
              query: getEntireUnitFlowFeedQuery,
              variables: feedQueryVariables,
            },
            data => {
              if (!data) return;

              const updatedEdges = _.map(unitFlowFeed, edge => {
                const { node } = edge;
                if (node.item?.id == itemId) node.label = label;
                return edge;
              });

              return update(data, {
                node: {
                  learningCourseFlowFeedWrapper: {
                    learningCourseFlowFeed: {
                      edges: { $set: updatedEdges },
                    },
                  },
                },
              });
            }
          );

          /*
            if resource update right pane resources pane
            if section update outline 
          */
          if (isResource) {
            cache.updateQuery(
              {
                query: getUnitCombinedLibraryQuery,
                variables: combinedLibraryFeedQueryVariables,
              },
              data => {
                if (!data) return;

                const combinedLibraryEdges = _.cloneDeep(
                  _.get(
                    data,
                    "node.learningCourseFlowFeedWrapper.learningCourseFlowFeed.edges",
                    []
                  )
                );

                const itemIdx = _.findIndex(
                  combinedLibraryEdges,
                  ({ node }) => node.item?.id === attachment?.id
                );

                if (itemIdx !== -1) {
                  const newItem = _.cloneDeep(combinedLibraryEdges[itemIdx]);

                  if (newItem) {
                    newItem.node.label = label;

                    if (attachment) {
                      const nodeAttachment = newItem.node.item;
                      nodeAttachment.name = label;
                      if (nodeAttachment.attachmentType === "LINK") {
                        nodeAttachment.url = attachment.url;
                        nodeAttachment.thumbUrl = attachment.thumbUrl;
                      } else if (nodeAttachment.attachmentType === "NOTE") {
                        nodeAttachment.name = label;
                        nodeAttachment.attachmentTitle = label;
                        nodeAttachment.title = label;
                      } else {
                        nodeAttachment.name = label;
                      }
                    }
                    return update(data, {
                      node: {
                        learningCourseFlowFeedWrapper: {
                          learningCourseFlowFeed: {
                            edges: {
                              [itemIdx]: { $set: newItem },
                            },
                          },
                        },
                      },
                    });
                  }
                }
              }
            );
          } else {
            cache.updateQuery(
              {
                query: getUnitFlowOutlineFeedQuery,
                variables: outlineQueryVariables,
              },
              data => {
                if (!data) return;

                const unitFlowFeedOutline = _.cloneDeep(
                  _.get(
                    data,
                    "node.learningCourseFlowFeedWrapper.learningCourseFlowFeedOutline",
                    []
                  )
                );
                const itemIdx = _.findIndex(unitFlowFeedOutline, { id });

                const item = unitFlowFeedOutline[itemIdx];
                const newItem = _.cloneDeep(item);

                if (!newItem) return;
                newItem.label = label;

                return update(data, {
                  node: {
                    learningCourseFlowFeedWrapper: {
                      learningCourseFlowFeedOutline: {
                        [itemIdx]: { $set: newItem },
                      },
                    },
                  },
                });
              }
            );
          }
        },
      });
      dispatch(setToastMsg(getToastMsgForResourceUpdate(resourceType)));
    } catch (error) {
      rollbackResourcesInCache({ rollbackResources });

      console.error(error);
      dispatchError({ dispatch, e: error });
    } finally {
      if (isInlineCreateOrEdit) {
        dispatch(setActiveContentData({}));
      }
    }
  };
};

export const deleteUnitFlowResources = ({
  ids: _ids,
  shouldDeleteSectionContents = false,
  deleteLinkedCopies = false,
  repeatedResources = [],
  isSection,
  groupType = "LEARNING_ENGAGEMENT",
}) => {
  return async (dispatch, getState) => {
    const ids = _.compact(_ids);
    if (_.isEmpty(ids)) return;

    const uniqueAssessmentSetIds = new Set();
    const uniqueUnitResourceIds = new Set();

    const state = getState();
    const unitPlanId = _.get(state, "planner.unitPlanData.unitPlanId", "");

    const filters = _.get(state, "unitFlow.filters", {});

    const refetchQueries = [
      {
        query: getUnitFlowItemCountInUnitPlanQuery,
        variables: { id: unitPlanId },
      },
    ];
    const learningCourseId = _.get(
      state,
      "planner.unitPlanData.learningCourseId",
      null
    );

    if (isSection) {
      const outlineQueryVariables = getUnitPlanOutlineFeedVariablesMemoized({
        unitPlanId,
        learningCourseId,
      });

      refetchQueries.push({
        query: getUnitFlowOutlineFeedQuery,
        variables: outlineQueryVariables,
      });
    }

    const userInfo = getUserInfo({ portalType: "PLANNER" });
    const curriculumType = _.get(
      state,
      "platform.currentCurriculumProgram.type",
      null
    );

    const feedQueryVariables = getUnitFlowFeedVariablesMemoized({
      unitPlanId,
      filters,
      userInfo,
      curriculumType,
      learningCourseId,
    });

    const cachedData = getEntireUnitFlowFeedQueryFromCache(feedQueryVariables);

    const cachedUnitFlowFeed = _.get(
      cachedData,
      "node.learningCourseFlowFeedWrapper.learningCourseFlowFeed",
      {}
    );

    const cachedUnitFlowFeedNodes = _.map(cachedUnitFlowFeed.edges, "node");

    const resource = getUnitFlowItemFromCache({
      id: _.first(ids),
      variables: feedQueryVariables,
    });

    const resourceType = _.get(resource, "resourceType", "");
    const isRepeatedResource = _.get(resource, "isRepeated", false);

    // pushes all the siblings of the resource to ids
    if (deleteLinkedCopies) {
      const id = _.get(resource, "id", "");
      const resourceItemId = _.get(resource, "item.id", "");

      _.forEach(cachedUnitFlowFeed.edges, ({ node }) => {
        const isRepeated = _.get(node, "isRepeated", false);
        const itemId = _.get(node, "item.id", "");
        if (isRepeated && itemId === resourceItemId && id !== node.id) {
          ids.push(node.id);
        }
      });
    }

    const itemIdsToDelete = [...ids, ...repeatedResources];

    // will contain resources which are going to be roll backed in case of error
    const rollbackResources = [];

    // if the resource is repeated, then we need to update the source resource
    if (isRepeatedResource) {
      removeRepeatedResourcesFromSourceFragment({
        itemId: _.get(resource, "item.id", ""),
        variables: feedQueryVariables,
        rollbackResources,
        itemIdsToDelete,
      });
    }

    // this will contain assessments list with their updated unitFlowResources
    const newRightPaneAssessmentsUnitFlowResources = [];

    // if resource is a source assessment itself
    if (resourceType == "ASSESSMENT" && !isRepeatedResource) {
      const assessmentId = _.get(resource, "item.id", "");
      newRightPaneAssessmentsUnitFlowResources.push({
        id: assessmentId,
        newUnitFlowResources: [],
      });
      uniqueAssessmentSetIds.add(assessmentId);
    } else if (resourceType === "FILE" && !isFolder(resource)) {
      const unitResourceId = _.get(resource, "item.id", "");
      uniqueUnitResourceIds.add(unitResourceId);
    }

    if (isFolder(resource) && shouldDeleteSectionContents) {
      const deletedItems = getNestedChildren({
        items: cachedUnitFlowFeedNodes,
        id: resource.id,
      });

      const nestedChildrenIds = _.map(deletedItems, "id");

      _.forEach(deletedItems, item => {
        const resourceType = _.get(item, "resourceType", "");

        if (resourceType == "ASSESSMENT") {
          const repeatedResourcesIds = _.get(item, "repeatedResources", []);
          const isRepeated = _.get(item, "isRepeated", false);

          const remainingRepeatedResourcesIds = _.difference(
            repeatedResourcesIds,
            nestedChildrenIds
          );

          // means this is the source assessment with repeatedResources in it
          const assessmentId = _.get(item, "item.id", "");
          if (!isRepeated) {
            // push the new repeated resources of the new source assessment
            newRightPaneAssessmentsUnitFlowResources.push({
              id: assessmentId,
              newUnitFlowResources: _.map(
                remainingRepeatedResourcesIds,
                id => ({
                  id,
                })
              ),
            });
            uniqueAssessmentSetIds.add(assessmentId);
          } else {
            removeRepeatedResourcesFromSourceFragment({
              itemId: assessmentId,
              variables: feedQueryVariables,
              rollbackResources,
              itemIdsToDelete: [item.id],
            });
          }

          if (!_.isEmpty(remainingRepeatedResourcesIds)) {
            // take first item as new source as it's the most recent and will become our new source
            const newSourceId = remainingRepeatedResourcesIds.shift();
            uniqueAssessmentSetIds.delete(assessmentId);
            const cachedNewSource = getUnitFlowItemFromCache({
              id: newSourceId,
              variables: feedQueryVariables,
            });

            if (cachedNewSource) {
              // used to rollback on error
              rollbackResources.push(cachedNewSource);

              const newSource = _.cloneDeep(cachedNewSource);

              newSource.isRepeated = false;
              newSource.repeatedResources = remainingRepeatedResourcesIds;

              writeUnitFlowItemInCache({
                id: newSourceId,
                variables: feedQueryVariables,
                data: newSource,
              });
            }
          }
        } else {
          const unitResourceId = _.get(item, "item.id", "");
          uniqueUnitResourceIds.add(unitResourceId);
        }
      });
    }

    //variables for updating right pane assessment pane
    const currentRightPane = _.get(state, "unitFlow.currentRightPane", "");

    const assessmentSearchText = _.get(
      state,
      "assessment.filters.searchText",
      ""
    );

    const assessmentTypes =
      ASSESSMENT_TYPE_PARENT_GROUP_MAPPING[currentRightPane] || [];

    const assessmentsFeedQueryVariables = {
      id: unitPlanId,
      filters: {
        searchText: assessmentSearchText,
        groupType,
        assessmentTypes,
      },
    };

    let countOfDeletedItems = 0;
    const rightPaneDeleteItemIds = [];
    try {
      await client.mutate({
        mutation: deleteEntityFlowResourceMutation,
        variables: {
          input: {
            ids,
            removeWithChildren: shouldDeleteSectionContents,
          },
        },
        optimisticResponse: {
          __typename: "Mutation",
          planner: {
            deleteLearningCourseFlowResource: true,
            __typename: "PlannerMutations",
          },
        },
        refetchQueries,
        update: cache => {
          cache.updateQuery(
            {
              query: getEntireUnitFlowFeedQuery,
              variables: feedQueryVariables,
            },
            data => {
              if (!data) return;

              const unitFlowFeed = _.cloneDeep(
                _.get(
                  data,
                  "node.learningCourseFlowFeedWrapper.learningCourseFlowFeed"
                )
              );

              const unitFlowFeedNodes = _.map(unitFlowFeed.edges, "node");

              const items = _.filter(unitFlowFeedNodes, ({ id }) =>
                _.includes(itemIdsToDelete, id)
              );

              // update children and count of parents
              _.forEach(items, item => {
                const parent = _.find(
                  unitFlowFeedNodes,
                  ({ id }) => id === item.parent?.id
                );

                if (parent) {
                  parent.childrenV2 = _.filter(
                    parent.childrenV2,
                    ({ id }) => id !== item.id
                  );

                  updateAncestorChildCountRecursively({
                    id: parent.id,
                    items: unitFlowFeed.edges,
                    factor: isFolder(item) ? 0 : -1,
                  });

                  recalculateMaxDepth({
                    items: unitFlowFeed.edges,
                    id: parent.id,
                  });
                }

                const nestedChildren = getNestedChildren({
                  items: unitFlowFeedNodes,
                  id: item.id,
                });

                if (isFolder(item)) {
                  if (shouldDeleteSectionContents) {
                    const nestedChildrenIds = _.map(nestedChildren, "id");
                    unitFlowFeed.edges = _.filter(
                      unitFlowFeed.edges,
                      ({ node }) => !_.includes(nestedChildrenIds, node.id)
                    );

                    if (parent) {
                      updateAncestorChildCountRecursively({
                        id: parent.id,
                        items: unitFlowFeed.edges,
                        factor: -_.get(
                          item,
                          "childrenHierarchyInfo.totalCount",
                          0
                        ),
                      });
                    }
                  } else {
                    // children should be pushed outwards after parent deletion
                    // so we decrease depth and change parent
                    _.forEach(nestedChildren, child => {
                      child.depth -= 1;

                      // only update parent of immediate children
                      // otherwise the remaining structure will come out flat
                      if (parent && child.parent?.id === item.id) {
                        parent.childrenV2.push(
                          getChildDataForCache({ node: child })
                        );

                        child.parent = {
                          ...(child.parent ?? {}),
                          id: parent.id,
                        };
                      }
                    });
                  }
                }
              });

              countOfDeletedItems = _.reduce(
                items,
                (result, item) => {
                  result += _.get(item, "childrenHierarchyInfo.totalCount", 0);
                  result += isFolder(item) ? 0 : 1;
                  return result;
                },
                0
              );

              const updatedFeed = {
                ...unitFlowFeed,
                edges: _.filter(
                  unitFlowFeed.edges,
                  ({ node }) => !_.includes(itemIdsToDelete, node.id)
                ),
                resourceTypeBasedCount: {
                  ...unitFlowFeed?.resourceTypeBasedCount,
                  totalCount:
                    _.get(
                      unitFlowFeed,
                      "resourceTypeBasedCount.totalCount",
                      0
                    ) - countOfDeletedItems,
                },
              };

              const rightPaneItems = _.get(
                state,
                "unitFlow.rightPaneItems",
                []
              );
              const combinedLibraryUniqueIds = new Set([
                ...uniqueAssessmentSetIds,
                ...uniqueUnitResourceIds,
              ]);

              const idsInRightPane = new Set();
              _.forEach(rightPaneItems, item => {
                const id = _.get(item, "item.id", "");
                idsInRightPane.add(id);
              });

              const intersection = _.intersection(
                _.toArray(idsInRightPane),
                _.toArray(combinedLibraryUniqueIds)
              );

              if (!_.isEmpty(intersection)) {
                _.forEach(intersection, id => {
                  if (
                    !_.find(
                      updatedFeed.edges,
                      ({ node }) => node.item?.id == id
                    )
                  )
                    rightPaneDeleteItemIds.push(id);
                });
              }

              return update(data, {
                node: {
                  learningCourseFlowFeedWrapper: {
                    learningCourseFlowFeed: { $set: updatedFeed },
                  },
                },
              });
            }
          );

          const combinedLibraryFeedQueryVariables = getCombinedLibraryFeedVariablesMemoized(
            {
              unitPlanId,
              filters: _.get(state, "unitPlans.combinedLibrary.filters", {}),
              learningCourseId,
            }
          );

          cache.updateQuery(
            {
              query: getUnitCombinedLibraryQuery,
              variables: combinedLibraryFeedQueryVariables,
            },
            data => {
              if (!data) return;
              const oldEdges = _.cloneDeep(
                _.get(
                  data,
                  "node.learningCourseFlowFeedWrapper.learningCourseFlowFeed.edges",
                  []
                )
              );
              const updatedEdges = _.filter(
                oldEdges,
                ({ node }) => !_.includes(rightPaneDeleteItemIds, node.item.id)
              );
              return update(data, {
                node: {
                  learningCourseFlowFeedWrapper: {
                    learningCourseFlowFeed: {
                      edges: { $set: updatedEdges },
                    },
                  },
                },
              });
            }
          );
        },
      });
    } catch (error) {
      console.error(error);

      // Revert optimistic data on error
      cache.updateQuery(
        {
          query: getEntireUnitFlowFeedQuery,
          variables: feedQueryVariables,
        },
        () => cachedData
      );

      // Revert resources on error
      rollbackResourcesInCache({ rollbackResources });

      dispatchError({ dispatch, e: error });
    }
  };
};

/**
 * This function removes deleted assignments from cache for both middlePane and rightPane
 * Used when an assessment / LE / worksheet is discarded on creation modal
 */
export const removeUnitFlowResourcesFromFeed = ({ ids }) => {
  return (dispatch, getState) => {
    const state = getState();

    const currentTab = _.get(state, "unitPlans.currentTab");
    const isUnitFlowTab = currentTab === "unitFlow";

    if (!isUnitFlowTab || !isUnitFlowRevampEnabled() || _.isEmpty(ids)) {
      return;
    }

    const curriculumType = _.get(
      state,
      "platform.currentCurriculumProgram.type",
      null
    );

    const userInfo = getUserInfo({ portalType: "PLANNER" });

    const unitPlanId = _.get(state, "planner.unitPlanData.unitPlanId", "");
    const filters = _.get(state, "unitFlow.filters", {});
    const learningCourseId = _.get(
      state,
      "planner.unitPlanData.learningCourseId",
      null
    );

    const feedQueryVariables = getUnitFlowFeedVariablesMemoized({
      unitPlanId,
      filters,
      userInfo,
      curriculumType,
      learningCourseId,
    });

    cache.updateQuery(
      {
        query: getEntireUnitFlowFeedQuery,
        variables: feedQueryVariables,
      },
      data => {
        if (!data) return;

        let countOfDeletedItems = 0;

        const unitFlowFeed = _.cloneDeep(
          _.get(
            data,
            "node.learningCourseFlowFeedWrapper.learningCourseFlowFeed"
          )
        );

        const unitFlowFeedEdges = unitFlowFeed.edges;
        const unitFlowFeedNodes = _.map(unitFlowFeedEdges, "node");

        const items = _.filter(unitFlowFeedNodes, node => {
          const itemId = _.get(node, "item.id");
          return _.includes(ids, itemId);
        });

        _.forEach(items, item => {
          const parent = _.find(
            unitFlowFeedNodes,
            ({ id }) => id === item.parent?.id
          );

          if (parent) {
            parent.childrenV2 = _.filter(
              parent.childrenV2,
              ({ id }) => id !== item.id
            );

            updateAncestorChildCountRecursively({
              id: parent.id,
              items: unitFlowFeedEdges,
              factor: -1,
            });

            recalculateMaxDepth({
              items: unitFlowFeedEdges,
              id: parent.id,
            });
          }
        });

        countOfDeletedItems = _.reduce(
          items,
          (result, item) => {
            result += _.get(item, "childrenHierarchyInfo.totalCount", 0);
            result += 1;
            return result;
          },
          0
        );

        const filteredEdges = _.filter(unitFlowFeedEdges, edge => {
          const itemId = _.get(edge, "node.item.id");
          return !_.includes(ids, itemId);
        });

        const updatedFeed = {
          ...unitFlowFeed,
          edges: filteredEdges,
          resourceTypeBasedCount: {
            ...unitFlowFeed?.resourceTypeBasedCount,
            totalCount:
              _.get(unitFlowFeed, "resourceTypeBasedCount.totalCount", 0) -
              countOfDeletedItems,
          },
        };

        return update(data, {
          node: {
            learningCourseFlowFeedWrapper: {
              learningCourseFlowFeed: { $set: updatedFeed },
            },
          },
        });
      }
    );

    const combinedLibraryFilters = _.get(
      state,
      "unitPlans.combinedLibrary.filters",
      {}
    );

    const combinedLibraryFeedQueryVariables = getCombinedLibraryFeedVariablesMemoized(
      {
        unitPlanId,
        filters: combinedLibraryFilters,
        learningCourseId,
      }
    );

    cache.updateQuery(
      {
        query: getUnitCombinedLibraryQuery,
        variables: combinedLibraryFeedQueryVariables,
      },
      data => {
        if (!data) return;

        const oldEdges = _.cloneDeep(
          _.get(
            data,
            "node.learningCourseFlowFeedWrapper.learningCourseFlowFeed.edges",
            []
          )
        );

        const updatedEdges = _.filter(
          oldEdges,
          ({ node }) => !_.includes(ids, node.item?.id)
        );

        return update(data, {
          node: {
            learningCourseFlowFeedWrapper: {
              learningCourseFlowFeed: {
                edges: { $set: updatedEdges },
              },
            },
          },
        });
      }
    );
  };
};

const getSharingToastMsg = ({
  newSharingState = SHARING_STATES.PUBLISHED,
  oldSharingState = SHARING_STATES.DRAFT,
  visibilityStats = true,
  addedClasses = [],
  deletedClasses = [],
  updatedClasses = [],
  shareParentOnly = true,
  resourceType = GENERIC_ITEM_TYPES.RESOURCE,
  associatedClasses = [],
  hasLockedAcademicTermChildren = false,
}) => {
  const t = i18n.t;
  const courseClassesCount = _.size(associatedClasses);
  const addedClassesCount = _.size(addedClasses);
  const deletedClassesCount = _.size(deletedClasses);
  const updatedClassesCount = _.size(updatedClasses);
  const itemsSkippedText =
    resourceType === GENERIC_ITEM_TYPES.FOLDER
      ? t("course:locked_grading_periond_items_were_skipped")
      : null;

  if (
    !shareParentOnly &&
    addedClassesCount === 0 &&
    deletedClassesCount === 0 &&
    updatedClassesCount === 0
  ) {
    let msg = t("course:children_settings_updated");
    if (hasLockedAcademicTermChildren && itemsSkippedText) {
      msg = `${msg} ${itemsSkippedText}`;
    }

    return msg;
  }

  // Determines whether the action is sharing or unsharing
  let isSharing =
    newSharingState === SHARING_STATES.PUBLISHED &&
    newSharingState !== oldSharingState;
  let isUnsharing =
    newSharingState === SHARING_STATES.DRAFT &&
    newSharingState !== oldSharingState;

  // Check if the item share settings are updating or not
  if (!isSharing && !isUnsharing) {
    if (
      addedClassesCount > 0 &&
      deletedClassesCount === 0 &&
      updatedClassesCount === 0
    ) {
      isSharing = true;
      isUnsharing = false;
    }

    if (
      deletedClassesCount > 0 &&
      addedClassesCount === 0 &&
      updatedClassesCount === 0
    ) {
      isSharing = false;
      isUnsharing = true;
    }
  }

  const isUpdating = !isSharing && !isUnsharing;

  // Retrieves resource type details and the appropriate message locale
  const getResourceTypeDetails = () => {
    const isUnit = resourceType === GENERIC_ITEM_TYPES.UNIT_PLAN;
    const isFolder = resourceType === GENERIC_ITEM_TYPES.FOLDER;
    const isAssessment = resourceType === GENERIC_ITEM_TYPES.ASSESSMENT;

    let type = t("common:resource_label");
    let msgLocale = "course:item_updated_with_action";

    if (isUnit) {
      type = t("common:unit");
      if (!shareParentOnly)
        msgLocale = "course:item_and_its_content_updated_with_action";
    } else if (isFolder) {
      type = t("common:section");
      if (!shareParentOnly)
        msgLocale = "course:item_and_its_content_updated_with_action";
    } else if (isAssessment) {
      type = t("common:assessment");
    }

    if (isUpdating) {
      msgLocale = "course:item_access_updated";
    }

    return { type, msgLocale };
  };

  // Generates a localized string for the number of classes
  const getClassText = (count, isAll) => {
    if (isAll) {
      return `${t("common:all_lower_case")} ${t(
        "common:class_lowercase_other"
      )}`;
    }

    return t("common:shared_with_class_count", { count });
  };

  const { type, msgLocale } = getResourceTypeDetails();
  const actionText = t(isSharing ? "common:shared" : "common:unshared");

  let msg,
    notVisibleText,
    classText,
    updatedEntitiesText,
    subActionText = t("common:modified");

  if (isSharing) {
    // Handles the case where the item is being shared
    const isSharingWithAll = addedClassesCount === courseClassesCount;
    classText = getClassText(addedClassesCount, isSharingWithAll);

    // Adds visibility information if applicable
    if (_.isObject(visibilityStats)) {
      const parentType = _.get(visibilityStats, "parentType", "");
      const parentText =
        parentType === "UNIT_PLAN" ? t("common:unit") : t("common:section");

      notVisibleText = t("course:item_shared_but_not_visible", {
        type,
        parentType: parentText,
      });
    }
  } else if (isUnsharing) {
    // Handles the case where the item is being unshared
    const isUnsharingWithAll = deletedClassesCount === courseClassesCount;
    classText = getClassText(deletedClassesCount, isUnsharingWithAll);
  } else {
    // Handle the case where the item share settings are updated

    // Count of all the added and removed students from all the updated classes
    const updatedStudentsCount = _.reduce(
      updatedClasses,
      (acc, cls) => {
        const addedStudents = _.get(cls, "addedStudents", []);
        const removedStudents = _.get(cls, "removedStudents", []);

        return acc + addedStudents.length + removedStudents.length;
      },
      0
    );

    // Count of all the added and removed classes
    const allUpdatedClassesCount = addedClassesCount + deletedClassesCount;

    const updatedEntities = [];

    if (allUpdatedClassesCount > 0) {
      const classesLocale =
        allUpdatedClassesCount > 1
          ? "common:class_lowercase_other"
          : "common:class_lowercase";
      updatedEntities.push(t(classesLocale));
    }

    if (updatedStudentsCount > 0) {
      const studentsLocale =
        updatedStudentsCount > 1
          ? "common:student_lowercase_other"
          : "common:student_lowercase";
      updatedEntities.push(t(studentsLocale));
    }

    // No classes or students were updated, then the unit-planner settings were updated
    if (updatedEntities.length === 0) {
      const studentsLocale = "common:student_lowercase_other";
      updatedEntities.push(t(studentsLocale));
      subActionText = t("common:selected");
    }

    updatedEntitiesText = updatedEntities.join(` ${t("common:and")} `);

    // Adds visibility information if applicable
    if (_.isObject(visibilityStats)) {
      const parentType = _.get(visibilityStats, "parentType", "");
      const parentText =
        parentType === "UNIT_PLAN" ? t("common:unit") : t("common:section");

      notVisibleText = t("course:item_shared_but_not_visible", {
        type,
        parentType: parentText,
      });
    }
  }

  msg = t(msgLocale, {
    type,
    action: actionText,
    classText,
    updatedEntitiesText,
    subAction: subActionText,
  });

  if (notVisibleText) {
    msg = `${msg} ${notVisibleText}`;
  }

  if (hasLockedAcademicTermChildren && itemsSkippedText) {
    msg = `${msg} ${itemsSkippedText}`;
  }

  return msg;
};

const getSharedClassStudentStructure = ({ details }) => {
  const sharedDetails = _.reduce(
    details,
    (acc, studentIds, classId) => {
      const clsDetails = {
        class: {
          id: classId,
          __typename: "Course",
        },
        students: _.map(studentIds, studentId => ({
          id: studentId,
          __typename: "Student",
        })),
        __typename: "LearningCourseFlowSharedClassStudentMap",
      };
      acc.push(clsDetails);
      return acc;
    },
    []
  );

  return sharedDetails;
};

const updateSharedDetails = ({
  currentSharedDetails,
  addedClasses,
  updatedClasses,
  deletedClasses,
}) => {
  const newSharedSettings = _.reduce(
    currentSharedDetails,
    (acc, cls) => {
      acc[cls.class.id] = _.map(cls.students, "id");
      return acc;
    },
    {}
  );

  // Filter out deleted classes
  _.forEach(deletedClasses, cls => {
    delete newSharedSettings[cls];
  });

  // Update existing classes
  _.forEach(updatedClasses, cls => {
    const classId = cls.classId;
    const sharedClassStudentIds = _.get(newSharedSettings, classId, []);

    // Add/Remove student only if class exists in the current shared details
    if (_.size(sharedClassStudentIds) > 0) {
      const selectedStudents = new Set(sharedClassStudentIds);
      const addedStudents = _.get(cls, "addedStudents", []);
      const removedStudents = _.get(cls, "removedStudents", []);

      _.forEach(addedStudents, studentId => {
        selectedStudents.add(studentId);
      });

      _.forEach(removedStudents, studentId => {
        selectedStudents.delete(studentId);
      });

      newSharedSettings[classId] = Array.from(selectedStudents);
    }
  });

  // Add new classes
  _.forEach(addedClasses, cls => {
    const classId = cls.classId;
    const selectedStudents = _.get(newSharedSettings, classId, []);
    const newStudents = _.get(cls, "studentIds", []);
    const studentIds = new Set([...selectedStudents, ...newStudents]);

    newSharedSettings[classId] = Array.from(studentIds);
  });

  return getSharedClassStudentStructure({ details: newSharedSettings });
};

const getUpdatedChildrenSharingState = ({
  currentSharedDetails = [],
  newSharedDetails = [],
  editableClassIds = [],
}) => {
  const nonEditableClasses = _.filter(
    currentSharedDetails,
    cls => !_.includes(editableClassIds, cls?.class?.id)
  );

  return [...nonEditableClasses, ...newSharedDetails];
};

export const shareUnitFlowContent = ({
  id,
  action = SHARE_ACTIONS.SHARE,
  sharingState = SHARING_STATES.PUBLISHED,
  addedClasses = [],
  deletedClasses = [],
  updatedClasses = [],
  shareParentOnly = true,
  associatedClasses = [],
  editableClasses = [],
}) => {
  return async (dispatch, getState) => {
    const state = getState();

    const unitPlanId = _.get(state, "planner.unitPlanData.unitPlanId", "");

    const filters = _.get(state, "unitFlow.filters", {});
    const userInfo = getUserInfo({ portalType: "PLANNER" });
    const curriculumType = _.get(
      state,
      "platform.currentCurriculumProgram.type",
      null
    );
    const learningCourseId = _.get(
      state,
      "planner.unitPlanData.learningCourseId",
      null
    );

    const variables = {
      input: {
        id,
        learningCourseId,
        action,
        addedClasses,
        deletedClasses,
        updatedClasses,
        state: sharingState,
        shareParentOnly,
      },
    };
    const feedQueryVariables = getUnitFlowFeedVariablesMemoized({
      unitPlanId,
      filters,
      userInfo,
      curriculumType,
      learningCourseId,
    });

    try {
      const currItem = getUnitFlowItemFromCache({
        id,
        variables: feedQueryVariables,
      });

      const itemOldSharingState = _.get(
        currItem,
        "state",
        SHARING_STATES.DRAFT
      );

      const isUnitOrSection = isModule(currItem);

      let resourceType;

      switch (true) {
        case isFolder(currItem):
          resourceType = GENERIC_ITEM_TYPES.FOLDER;
          break;
        case isAssessment(currItem):
          resourceType = GENERIC_ITEM_TYPES.ASSESSMENT;
          break;
        default:
          resourceType = GENERIC_ITEM_TYPES.RESOURCE;
      }

      let itemSharingState = sharingState;
      let visibilityStats = true;

      // First, update individual items in cache
      const isSharing = action === SHARE_ACTIONS.SHARE;
      const currentSharedDetails = _.get(currItem, "sharedDetails", []);

      const updatedSharedDetails = isSharing
        ? updateSharedDetails({
            currentSharedDetails,
            addedClasses,
            updatedClasses,
            deletedClasses,
          })
        : [];

      itemSharingState =
        _.size(updatedSharedDetails) > 0 ? sharingState : SHARING_STATES.DRAFT;

      const newItem = {
        ...currItem,
        state: itemSharingState,
        sharedDetails: updatedSharedDetails,
      }; // Get unit flow feed from cache
      const cachedData = getEntireUnitFlowFeedQueryFromCache(
        feedQueryVariables
      );
      if (!cachedData) return;

      // Save reference to original cached data for rollback purposes (no need to clone)
      const originalCachedData = cachedData;

      // Save reference to original item for rollback purposes (no need to clone)
      const originalItem = currItem;

      // Create a list to track all modified child items for potential rollback
      const modifiedChildItems = [];

      const unitFlowFeed = _.get(
        cachedData,
        "node.learningCourseFlowFeedWrapper.learningCourseFlowFeed",
        {}
      );
      if (_.isEmpty(unitFlowFeed)) return;

      const itemIdx = _.findIndex(
        unitFlowFeed.edges,
        ({ node }) => node.id === id
      );

      if (itemIdx === -1) return;

      // Update the sharing state of the item in the whole feed
      let updatedFeed = update(unitFlowFeed, {
        edges: {
          [itemIdx]: {
            node: {
              state: { $set: itemSharingState },
            },
          },
        },
      });

      // Update the sharing state and sharing settings of the item
      writeUnitFlowItemInCache({
        id,
        variables: feedQueryVariables,
        data: newItem,
      });

      const courseFlowFeedNodes = _.map(updatedFeed.edges, "node");

      let hasLockedAcademicTermChildren = false;

      // Update the sharing state/settings of the children
      if (isUnitOrSection) {
        if (!shareParentOnly) {
          const editableClassIds = _.map(editableClasses, "id");
          const newEditableClassesForChildren = _.filter(
            updatedSharedDetails,
            cls => _.includes(editableClassIds, cls?.class?.id)
          );

          const nestedChildren = getNestedChildren({
            items: courseFlowFeedNodes,
            id,
          });
          // Update the sharing state of children
          _.forEach(nestedChildren, child => {
            const academicTerm = _.get(child, "item.academicTerm");
            const assessmentType = _.get(child, "item.assessmentType.value");
            const isGradingPeriodUnlocked = getIsGradingPeriodUnlocked(
              academicTerm,
              assessmentType
            );
            hasLockedAcademicTermChildren =
              hasLockedAcademicTermChildren || !isGradingPeriodUnlocked;

            // If grading period is locked then the item's shared settings cannot be updated
            if (!isGradingPeriodUnlocked) return;

            const childItem = getUnitFlowItemFromCache({
              id: child.id,
              variables: feedQueryVariables,
            });

            // Save original child item for potential rollback
            modifiedChildItems.push({
              id: child.id,
              originalData: childItem,
            });

            const childIndex = _.findIndex(
              unitFlowFeed.edges,
              ({ node }) => node.id === child.id
            );

            const childSharedDetails = _.get(childItem, "sharedDetails", []);

            const updatedChildSharedDetails = getUpdatedChildrenSharingState({
              currentSharedDetails: childSharedDetails,
              newSharedDetails: newEditableClassesForChildren,
              editableClassIds,
            });

            const childCurrentSharingState = _.get(
              childItem,
              "state",
              SHARING_STATES.DRAFT
            );

            const childSharingState =
              _.size(updatedChildSharedDetails) > 0
                ? isSharing
                  ? SHARING_STATES.PUBLISHED
                  : childCurrentSharingState
                : SHARING_STATES.DRAFT;

            const childNewItem = {
              ...childItem,
              state: childSharingState,
              sharedDetails: updatedChildSharedDetails,
            };

            // Update the sharing state of the child item in the whole feed
            updatedFeed = update(updatedFeed, {
              edges: {
                [childIndex]: {
                  node: {
                    state: { $set: childSharingState },
                  },
                },
              },
            });

            writeUnitFlowItemInCache({
              id: child.id,
              variables: feedQueryVariables,
              data: childNewItem,
            });
          });
        }
      }

      if (itemSharingState === SHARING_STATES.PUBLISHED) {
        const ancestors = getAncestors({
          items: courseFlowFeedNodes,
          id,
        });

        const ancestorsWithSharingDetails = _.map(ancestors, ancestor =>
          getUnitFlowItemFromCache({
            id: ancestor.id,
            variables: feedQueryVariables,
          })
        );

        visibilityStats = getIsResourceSharedRecursivelyWithSameSettings({
          orgResource: newItem,
          items: ancestorsWithSharingDetails,
        });
      }

      // Update the whole feed in cache
      cache.writeQuery({
        query: getEntireUnitFlowFeedQuery,
        variables: feedQueryVariables,
        data: update(cachedData, {
          node: {
            learningCourseFlowFeedWrapper: {
              learningCourseFlowFeed: {
                edges: { $set: updatedFeed.edges },
              },
            },
          },
        }),
      });

      // [Don't remove] Hack to update the shared details in the UI
      dispatch(setActiveContentData({}));

      try {
        // Now perform the actual mutation
        await client.mutate({
          mutation: shareEntityFlowResourceMutation,
          variables,
          optimisticResponse: {
            __typename: "Mutation",
            planner: {
              shareLearningCourseFlowResource: true,
              __typename: "PlannerMutations",
            },
          },
        });
        dispatch(
          setToastMsg({
            msg: getSharingToastMsg({
              action,
              newSharingState: itemSharingState,
              oldSharingState: itemOldSharingState,
              visibilityStats,
              addedClasses,
              deletedClasses,
              updatedClasses,
              shareParentOnly,
              resourceType,
              associatedClasses,
              hasLockedAcademicTermChildren,
            }),
            notString: true,
            type: "success",
            position: "toast-bottom-left",
          })
        );
      } catch (error) {
        // Revert item cache
        writeUnitFlowItemInCache({
          id,
          variables: feedQueryVariables,
          data: originalItem,
        });

        // Revert all child items
        _.forEach(modifiedChildItems, ({ id, originalData }) => {
          writeUnitFlowItemInCache({
            id,
            variables: feedQueryVariables,
            data: originalData,
          });
        });

        // Revert the feed query cache
        cache.writeQuery({
          query: getEntireUnitFlowFeedQuery,
          variables: feedQueryVariables,
          data: originalCachedData,
        });

        console.error(error);
        dispatchError({ dispatch, e: error });
      }
      return;
    } catch (error) {
      console.error(error);
      dispatchError({ dispatch, e: error });
    }
  };
};

export const handleQuickCreateAndEdit = ({
  item,
  isSaving = true,
  quickCreation = false,
  setNewDataIds,
  isInlineCreation = true,
  templateId,
  taskTypeId,
}) => {
  return async (dispatch, getState) => {
    const state = getState();
    const {
      type,
      putAfterItemId,
      label,
      isFolderCollapsed,
      sectionCreationType,
      parent,
      depth,
      shouldAddToBottom = false,
    } = item;

    if (isSaving && (Boolean(label.trim()) || !isInlineCreation)) {
      if (!quickCreation) {
        dispatch(
          setActiveContentData({
            ...item,
            isSavingInline: true,
          })
        );
      }

      const parentId = parent?.id;

      switch (type) {
        case "CREATE_SECTION": {
          if (quickCreation) {
            dispatch(getResourceCreationToast({}));
          }
          dispatch(
            createUnitFlowResources({
              putAfterItemId,
              setNewDataIds,
              resources: [
                {
                  label,
                  itemType: "FOLDER",
                },
              ],
              isFolderCollapsed,
              quickCreation,
              sectionCreationType,
              parentId,
              newDepth: depth,
              shouldAddToBottom,
            })
          );

          break;
        }

        case "FILE_RENAME":
        case "SECTION_RENAME": {
          dispatch(
            updateUnitFlowResources({
              id: item.id,
              label,
              resourceType: type == "FILE_RENAME" ? "FILE" : "SECTION",
              attachment: _.get(item, "attachment", null),
            })
          );
          break;
        }
        default: {
          if (item?.label || !isInlineCreation) {
            if (quickCreation && isInlineCreation) {
              dispatch(getAssessmentCreationToast());
            }
            const unitPlanId = _.get(
              state,
              "planner.unitPlanData.unitPlanId",
              ""
            );
            const selectedCurriculum = getCurrentCurriculumProgramType(state);
            const isDP = _.isEqual(CURRICULUM_TYPE_DP, selectedCurriculum);
            const academicYearId = getSelectedAcademicYearId(state);
            const courseGradeFilters = {
              academicYearId,
            };
            const course = getCourseDetailsFromCache({
              id: item.courseId,
              courseGradeFilters,
            });
            try {
              const assessmentType = _.get(item, "item.assessmentType", {});
              const groupType = _.get(item, "item.type", null);
              const fields = [];
              if (isDP && groupType !== "QUICK_TASK") {
                const subjectLevels = _.filter(
                  _.get(course, "genericTags", []),
                  {
                    type: "SUBJECT_LEVEL",
                  }
                );
                if (_.size(subjectLevels) == 1) {
                  fields.push({
                    uid: "subjectLevel",
                    value: [_.get(subjectLevels[0], "id", null)],
                  });
                }
              }
              const assessment = await dispatch(
                getCreateAssessmentInputData({
                  assessmentType: _.get(assessmentType, "value", "le"),
                  title: item.label,
                  groupType,
                  unitPlanObj: {
                    unitPlanId,
                  },
                  course,
                  fields,
                  templateId,
                  taskTypeId,
                })
              );

              if (assessment) {
                return dispatch(
                  createUnitFlowResources({
                    putAfterItemId,
                    setNewDataIds,
                    resources: [
                      {
                        label,
                        itemType: "ASSESSMENT",
                        itemInput: {
                          assessment,
                        },
                      },
                    ],
                    isFolderCollapsed,
                    quickCreation,
                    sectionCreationType,
                    parentId,
                    newDepth: depth,
                    shouldAddToBottom,
                    showLoader: !isInlineCreation,
                    showToast: isInlineCreation,
                  })
                );
              }
            } catch (e) {
              dispatch(setActiveContentData({}));
              console.error(e);
            }
          }
        }
      }
    } else {
      dispatch(setActiveContentData({}));
    }
  };
};

export const duplicateUnitFlowResources = args => {
  return async (dispatch, getState) => {
    const {
      id,
      label,
      depth,
      parentId,
      putAfterItemId,
      setNewDataIds,
      addToTop = false,
      showRightPaneLoader = false,
      setIsCombinedLibraryActionLoading = () => {},
    } = args;
    const state = getState();
    const userInfo = getUserInfo({ portalType: "PLANNER" });
    const unitPlanId = _.get(state, "planner.unitPlanData.unitPlanId", "");
    const filters = _.get(state, "unitFlow.filters", {});

    const curriculumType = getCurrentCurriculumProgramType(state);
    const learningCourseId = _.get(
      state,
      "planner.unitPlanData.learningCourseId",
      null
    );
    const combinedLibraryFilters = _.get(
      state,
      "unitPlans.combinedLibrary.filters",
      {}
    );

    // variables for updating right pane
    const combinedLibraryFeedQueryVariables = getCombinedLibraryFeedVariablesMemoized(
      {
        unitPlanId,
        filters: combinedLibraryFilters,
        learningCourseId,
      }
    );
    const feedQueryVariables = getUnitFlowFeedVariablesMemoized({
      unitPlanId,
      filters,
      userInfo,
      curriculumType,
      learningCourseId,
    });

    const unitFlowFeed = _.get(
      getEntireUnitFlowFeedQueryFromCache(feedQueryVariables),
      "node.learningCourseFlowFeedWrapper",
      {}
    );
    const rightPaneQueryData = cache.readQuery({
      query: getUnitCombinedLibraryQuery,
      variables: combinedLibraryFeedQueryVariables,
    });

    const rightPaneFeed = _.get(
      rightPaneQueryData,
      "node.learningCourseFlowFeedWrapper",
      ""
    );
    const allItems =
      _.get(unitFlowFeed, "learningCourseFlowFeed.edges") ||
      _.get(rightPaneFeed, "learningCourseFlowFeed.edges", []);
    const item = _.find(allItems, ["node.id", id]);

    // we will return if the item is not there in cache of middlePane or rightPane
    if (!item) return;

    let _parentId = _.has(args, "parentId")
      ? parentId
      : _.get(item, "node.parent.id");
    if (!_parentId) {
      const learningCourseFlowResource = getLearningCourseFlowResourceFromCache(
        { id: unitPlanId }
      );
      _parentId = _.get(
        learningCourseFlowResource,
        "node.learningCourseFlowResource.id",
        null
      );
    }
    const _depth = _.has(args, "depth") ? depth : _.get(item, "node.depth", 1);
    const _putAfterItemId = _.has(args, "putAfterItemId") ? putAfterItemId : id;

    const curriculumProgramId = getCurriculumProgramIdFromState({ state });

    const input = {
      sourceId: learningCourseId,
      destinationId: learningCourseId,
      includedIds: [id],
      parentId: _parentId,
      putAfterItemId: _putAfterItemId,
      curriculumProgramId,
      label,
      isDuplicateResource: true,
      addToTop,
    };

    if (showRightPaneLoader) {
      setIsCombinedLibraryActionLoading(true);
    } else {
      dispatch(setIsActionLoading(true));
    }

    try {
      const cachedData = getEntireUnitFlowFeedQueryFromCache(
        feedQueryVariables
      );
      const unitFlowFeed = _.cloneDeep(
        _.get(
          cachedData,
          "node.learningCourseFlowFeedWrapper.learningCourseFlowFeed",
          {}
        )
      );
      const { data } = await client.mutate({
        mutation: importEntityFlowResourceMutation,
        variables: {
          input: {
            ...input,
            maxDepth: MAX_DEPTH,
          },
          syncedBluePrintItemFilters: {
            learningCourseId,
          },
        },
        update: (
          cache,
          {
            data: {
              planner: { importLearningCourseFlowResource },
            },
          }
        ) => {
          if (!importLearningCourseFlowResource?.isSuccess) return;
          const parentId = _.get(input, "parentId", _parentId);
          const resource = _.get(
            importLearningCourseFlowResource,
            "courseFlowResource",
            {}
          );
          const resourceType = _.get(resource, "resourceType", "");

          cache.updateQuery(
            {
              query: getEntireUnitFlowFeedQuery,
              variables: feedQueryVariables,
            },
            data => {
              if (!data) return;

              const repeatedItem = {
                __typename: "LearningCourseFlowEdge",
                node: {
                  ..._.get(
                    importLearningCourseFlowResource,
                    "courseFlowResource",
                    {}
                  ),
                  depth: _depth,
                },
              };

              const parent = _.find(
                unitFlowFeed.edges,
                ({ node }) => node.id === parentId
              )?.node;

              if (parent) {
                const { node } = repeatedItem;
                const createdResourcesAsChild = {
                  __typename: "LearningCourseFlow",
                  ...getChildDataForCache({ node }),
                };

                parent.childrenV2 = [
                  createdResourcesAsChild,
                  ...parent.childrenV2,
                ];

                updateAncestorChildCountRecursively({
                  id: parent.id,
                  items: unitFlowFeed.edges,
                  factor: 1,
                });
              }

              // If putAfterItemId is provided, add after that item
              if (_putAfterItemId) {
                const itemIndex = _.findIndex(unitFlowFeed.edges, [
                  "node.id",
                  _putAfterItemId,
                ]);

                if (itemIndex !== -1) {
                  unitFlowFeed.edges.splice(itemIndex + 1, 0, repeatedItem);
                }
              } else {
                unitFlowFeed.edges = addToTop
                  ? [repeatedItem, ...unitFlowFeed.edges]
                  : [...unitFlowFeed.edges, repeatedItem];
              }

              const updatedFeed = {
                ...unitFlowFeed,
                resourceBasedCount: {
                  ...unitFlowFeed.resourceBasedCount,
                  totalCount:
                    _.get(unitFlowFeed, "resourceBasedCount.totalCount", 0) + 1,
                },
              };

              setNewDataIds?.([resource.id]);
              return update(data, {
                node: {
                  learningCourseFlowFeedWrapper: {
                    learningCourseFlowFeed: { $set: updatedFeed },
                  },
                },
              });
            }
          );

          cache.updateQuery(
            {
              query: getUnitCombinedLibraryQuery,
              variables: combinedLibraryFeedQueryVariables,
            },
            data => {
              if (!data) return;

              const oldEdges = _.cloneDeep(
                _.get(
                  data,
                  "node.learningCourseFlowFeedWrapper.learningCourseFlowFeed.edges",
                  []
                )
              );
              let updatedEdges = [];

              if (resourceType === "ASSESSMENT") {
                const newEdge = {
                  node: convertToCombinedLibraryResource({
                    resource: resource,
                  }),
                  __typename: "LearningCourseFlowEdge",
                };
                updatedEdges = [newEdge, ...oldEdges];
              } else {
                const newEdge = {
                  node: convertToCombinedLibraryResource({
                    resource: resource,
                  }),
                  __typename: "LearningCourseFlowEdge",
                };
                updatedEdges = [newEdge, ...oldEdges];
              }

              return update(data, {
                node: {
                  learningCourseFlowFeedWrapper: {
                    learningCourseFlowFeed: {
                      edges: { $set: updatedEdges },
                    },
                  },
                },
              });
            }
          );
        },
      });

      const response = _.get(
        data,
        "planner.importLearningCourseFlowResource",
        false
      );

      if (response) {
        const { isSuccess } = response;

        if (!isSuccess) {
          dispatch(
            setToastMsg({
              msg: "toastMsgs:something_went_wrong",
              position: "toast-bottom-left",
            })
          );
        }
      }

      return response;
    } catch (e) {
      console.error(e);
      dispatchError({ dispatch, e });
    } finally {
      if (showRightPaneLoader) {
        setIsCombinedLibraryActionLoading(false);
      } else {
        dispatch(setIsActionLoading(false));
      }
    }
  };
};

export const repeatItem = args => {
  return async (dispatch, getState) => {
    const {
      item,
      depth,
      parentId,
      putAfterItemId,
      setNewDataIds,
      addToTop = false,
    } = args;
    const state = getState();
    const unitPlanId = _.get(state, "planner.unitPlanData.unitPlanId", "");
    const id = _.get(item, "id", "");
    const _depth = _.has(args, "depth") ? depth : _.get(item, "depth", 1);
    let _parentId = _.has(args, "parentId")
      ? parentId
      : _.get(item, "parent.id", null);
    if (!_parentId) {
      const learningCourseFlowResource = getLearningCourseFlowResourceFromCache(
        { id: unitPlanId }
      );
      _parentId = _.get(
        learningCourseFlowResource,
        "node.learningCourseFlowResource.id",
        null
      );
    }
    const _putAfterItemId = _.has(args, "putAfterItemId") ? putAfterItemId : id;
    if (!id) return;

    const filters = _.get(state, "unitFlow.filters", {});
    const userInfo = getUserInfo({ portalType: "PLANNER" });
    const curriculumType = _.get(
      state,
      "platform.currentCurriculumProgram.type",
      null
    );
    const learningCourseId = _.get(
      state,
      "planner.unitPlanData.learningCourseId",
      null
    );
    const feedQueryVariables = getUnitFlowFeedVariablesMemoized({
      unitPlanId,
      filters,
      userInfo,
      curriculumType,
      learningCourseId,
    });

    const cachedData = getEntireUnitFlowFeedQueryFromCache(feedQueryVariables);
    const cachedUnitFlowFeed = _.get(
      cachedData,
      "node.learningCourseFlowFeedWrapper.learningCourseFlowFeed",
      {}
    );

    const cachedParent = getUnitFlowItemFromCache({
      id: _parentId,
      variables: feedQueryVariables,
    });

    const optimisticNode = {
      ..._.omit(item, "attachment"),
      id: generateRandomId(),
      parent: cachedParent,
      isRepeated: true,
      childrenV2: [],
      repeatedResources: [],
      displaySequence: 0,
      depth: null,
      lockingInfo: null,
      isSyncedFromBluePrintCourse: false,
      isSyncedWithAllTeacherCourseV2: {},
      item: {
        ..._.get(item, "item", {}),
        type: "",
      },
    };

    /*
      Writes the optimistic node in cache
      Prevents skeleton in case of optimstic response
    */
    writeUnitFlowItemInCache({
      id: optimisticNode.id,
      variables: feedQueryVariables,
      data: optimisticNode,
    });

    const variables = {
      input: {
        id,
        parentId: _parentId,
        putAfterItemId: _putAfterItemId,
        addToTop,
      },
      filters: _.get(feedQueryVariables, "filters", {}),
      syncedBluePrintItemFilters: {
        learningCourseId,
      },
    };

    try {
      await client.mutate({
        mutation: repeatEntityFlowResourceMutation,
        variables,
        optimisticResponse: {
          planner: {
            repeatLearningCourseFlowResource: {
              isSuccess: true,
              error: "",
              courseFlowResource: optimisticNode,
              __typename: "LearningCourseFlowResourceSuccessErrorResponse",
            },
            __typename: "PlannerMutations",
          },
          __typename: "Mutation",
        },
        update: (
          cache,
          {
            data: {
              planner: { repeatLearningCourseFlowResource },
            },
          }
        ) => {
          if (
            _.isEmpty(cachedUnitFlowFeed) ||
            _.isEmpty(repeatLearningCourseFlowResource)
          )
            return;

          const unitFlowFeed = _.cloneDeep(cachedUnitFlowFeed);

          cache.updateQuery(
            {
              query: getEntireUnitFlowFeedQuery,
              variables: feedQueryVariables,
            },
            data => {
              if (!data) return;

              const repeatedItem = {
                __typename: "LearningCourseFlowEdge",
                node: {
                  ..._.get(
                    repeatLearningCourseFlowResource,
                    "courseFlowResource",
                    {}
                  ),
                  depth: _depth,
                },
              };

              const parent = _.cloneDeep(cachedParent);
              if (parent) {
                const { node } = repeatedItem;
                const createdResourcesAsChild = {
                  __typename: "LearningCourseFlow",
                  ...getChildDataForCache({ node }),
                };

                parent.childrenV2 = [
                  createdResourcesAsChild,
                  ...parent.childrenV2,
                ];

                updateAncestorChildCountRecursively({
                  id: parent.id,
                  items: unitFlowFeed.edges,
                  factor: 1,
                });
              }

              const sourceResourceEdge = findSourceResource({
                unitFlowEdges: unitFlowFeed.edges,
                itemId: _.get(item, "item.id"),
              });

              if (sourceResourceEdge) {
                const repeatedItemId = _.get(repeatedItem, "node.id", "");
                sourceResourceEdge.node.repeatedResources.push(repeatedItemId);
              }

              // If putAfterItemId is provided, add after that item
              if (_putAfterItemId && !addToTop) {
                const itemIndex = _.findIndex(unitFlowFeed.edges, [
                  "node.id",
                  _putAfterItemId,
                ]);

                if (itemIndex !== -1) {
                  unitFlowFeed.edges.splice(itemIndex + 1, 0, repeatedItem);
                }
              } else {
                // Otherwise, handle based on addToTop flag

                unitFlowFeed.edges = addToTop
                  ? [repeatedItem, ...unitFlowFeed.edges]
                  : [...unitFlowFeed.edges, repeatedItem];
              }

              const updatedFeed = {
                ...unitFlowFeed,
                resourceBasedCount: {
                  ...unitFlowFeed.resourceBasedCount,
                  totalCount:
                    _.get(unitFlowFeed, "resourceBasedCount.totalCount", 0) + 1,
                },
              };

              setNewDataIds?.([_.get(repeatedItem, "node.id", "")]);
              return update(data, {
                node: {
                  learningCourseFlowFeedWrapper: {
                    learningCourseFlowFeed: { $set: updatedFeed },
                  },
                },
              });
            }
          );
        },
      });

      const assessmentLabel = _.get(item, "item.title.value", "");

      dispatch(
        setToastMsg({
          type: "info",
          msg: "toastMsgs:connected_view_added_to_unit_flow",
          hasHTMLTags: true,
          showIcon: false,
          locale_params: [
            {
              key: "label",
              value: assessmentLabel,
              isPlainText: true,
            },
          ],
        })
      );
    } catch (error) {
      console.error(error);
      dispatchError({ dispatch, e: error });
    }
  };
};

export const importResourcesFromUnitFlow = ({
  input,
  maxDepth,
  shouldAddToBottom = false,
}) => {
  return async (dispatch, getState) => {
    const state = getState();
    const userInfo = getUserInfo({ portalType: "PLANNER" });
    const unitPlanId = _.get(state, "planner.unitPlanData.unitPlanId", "");
    const filters = _.get(state, "unitFlow.filters", {});

    const curriculumType = getCurrentCurriculumProgramType(state);
    const learningCourseId = _.get(
      state,
      "planner.unitPlanData.learningCourseId",
      null
    );

    let _parentId = _.get(input, "parentId", null);

    if (!_parentId) {
      const learningCourseFlowResource = getLearningCourseFlowResourceFromCache(
        { id: unitPlanId }
      );
      _parentId = _.get(
        learningCourseFlowResource,
        "node.learningCourseFlowResource.id",
        null
      );
    }

    const feedQueryVariables = getUnitFlowFeedVariablesMemoized({
      unitPlanId,
      filters,
      userInfo,
      curriculumType,
      learningCourseId,
    });

    const isRightPaneOpen =
      _.get(state, "unitPlans.customRightPane", "") === "COMBINED_LIBRARY";
    // variables for updating right pane
    const combinedLibraryFilters = _.get(
      state,
      "unitPlans.combinedLibrary.filters",
      {}
    );

    const combinedLibraryFeedQueryVariables = getCombinedLibraryFeedVariablesMemoized(
      {
        unitPlanId,
        filters: combinedLibraryFilters,
        learningCourseId,
      }
    );

    dispatch(setIsActionLoading(true));
    const outlineQueryVariables = getUnitPlanOutlineFeedVariablesMemoized({
      unitPlanId,
      filters,
      userInfo,
      curriculumType,
      learningCourseId,
    });

    const putAfterItemId = _.get(input, "putAfterItemId", null);

    const defaultPositioningOfNewItems = _.get(
      state,
      "unitFlow.displaySettings.add_to_top",
      false
    );

    const _addToTop = shouldAddToBottom ? false : defaultPositioningOfNewItems;

    const shouldAddtoTop = _addToTop && !putAfterItemId;

    const variables = {
      input: {
        ...input,
        addToTop: shouldAddtoTop,
        parentId: _parentId,
        maxDepth: MAX_DEPTH,
      },
      filters: _.get(feedQueryVariables, "filters", {}),
      syncedBluePrintItemFilters: {
        learningCourseId,
      },
    };

    try {
      const refetchQueries = [
        {
          query: getEntireUnitFlowFeedQuery,
          variables: feedQueryVariables,
        },
        {
          query: getUnitFlowOutlineFeedQuery,
          variables: outlineQueryVariables,
        },
        {
          query: getUnitFlowItemCountInUnitPlanQuery,
          variables: { id: unitPlanId },
        },
      ];
      // Only add combined library query if right pane is open
      if (isRightPaneOpen) {
        refetchQueries.push({
          query: getUnitCombinedLibraryQuery,
          variables: combinedLibraryFeedQueryVariables,
        });
      }
      const { data } = await client.mutate({
        mutation: importEntityFlowResourceMutation,
        variables,
        awaitRefetchQueries: true,
        refetchQueries,
      });

      const response = _.get(
        data,
        "planner.importLearningCourseFlowResource",
        false
      );

      if (response) {
        const { isSuccess, error } = response;
        if (!isSuccess) {
          if (error == "IMPORT_DEPTH_ERROR") {
            dispatch(
              setToastMsg({
                msg: "classMaterials:disabled_overlay_text",
                locale_params: [
                  {
                    key: "maxDepth",
                    value: maxDepth,
                    isPlainText: true,
                  },
                ],
                position: "toast-bottom-left",
              })
            );
          } else {
            dispatch(
              setToastMsg({
                msg: "toastMsgs:something_went_wrong",
                position: "toast-bottom-left",
              })
            );
          }
        }
      }

      return response;
    } catch (e) {
      console.error(e);
      dispatchError({ dispatch, e });
    } finally {
      dispatch(setIsActionLoading(false));
    }
  };
};

export const getLockingActionToastMsg = ({
  isLocked,
  updateSelf,
  updateChildren,
  entityLabelLocale = "common:resource_label",
  hasConflict = false,
}) => {
  const t = i18n.t;
  let locale = "course:locking_action_item_toast";
  const lockingAction = isLocked ? t("common:locked") : t("common:unlocked");
  const subEntity = t("course:all_items");

  if (updateSelf && updateChildren) {
    locale = "course:locking_action_item_with_children_toast";
  } else if (!updateSelf && updateChildren) {
    locale = "course:locking_action_children_toast";
  }

  if (isLocked && getIsBlueprintSyncHistoryV2Enabled() && hasConflict) {
    locale = `${locale}_skip`;
  }

  return t(locale, {
    lockingAction,
    entityType: t(entityLabelLocale),
    subEntity,
  });
};

export const updateLockedState = ({ type, id, assessmentTypes }) => {
  if (_.isEmpty(type) || _.isEmpty(id)) return;
  return async (dispatch, getState) => {
    const t = i18n.t;
    const state = getState();
    const academicYearId = getSelectedAcademicYearId(state);
    const learningCourseId = _.get(
      state,
      "planner.unitPlanData.learningCourseId",
      null
    );

    let lockScope = LOCKING_SCOPE.SELF,
      entityId = id,
      entityType = "LEARNING_COURSE_FLOW";

    const isLocked = !_.includes(type, "UNLOCK");

    switch (type) {
      case LOCKING_ACTIONS.LOCK_SECTION_AND_ITEMS:
      case LOCKING_ACTIONS.UNLOCK_SECTION_AND_ITEMS: {
        lockScope = LOCKING_SCOPE.SELF_AND_CHILDREN;
        break;
      }

      case LOCKING_ACTIONS.LOCK_ITEMS_ONLY:
      case LOCKING_ACTIONS.UNLOCK_ITEMS_ONLY: {
        lockScope = LOCKING_SCOPE.CHILDREN;
        break;
      }

      default: {
        lockScope = LOCKING_SCOPE.SELF;
      }
    }

    // This will be used for optimistic updates
    let updateSelf = _.includes(lockScope, LOCKING_SCOPE.SELF);
    const updateChildren = _.includes(lockScope, LOCKING_SCOPE.CHILDREN);

    const userInfo = getUserInfo({ portalType: "PLANNER" });
    const curriculumType = _.get(
      state,
      "platform.currentCurriculumProgram.type",
      null
    );

    const unitPlanId = _.get(state, "planner.unitPlanData.unitPlanId", "");
    const filters = _.get(state, "unitFlow.filters", {});
    const feedQueryVariables = getUnitFlowFeedVariablesMemoized({
      unitPlanId,
      filters,
      userInfo,
      curriculumType,
      learningCourseId,
    });

    try {
      const currItem = getUnitFlowItemFromCache({
        id,
        variables: feedQueryVariables,
      });

      const isSection = isFolder(currItem);

      let updatedItem = currItem;
      const isItemConflicted = getHasConflict({ item: currItem });
      let hasAnyConflict = isItemConflicted;

      if (!getIsBlueprintSyncHistoryV2Enabled()) {
        updateSelf = updateSelf && !isItemConflicted;
      }

      // Get unit flow feed from cache
      const cachedData = getEntireUnitFlowFeedQueryFromCache(
        feedQueryVariables
      );

      if (!cachedData) return;

      // Save reference to original cached data for potential rollback (no need to clone)
      const originalCachedData = cachedData;

      // Save reference to original item for potential rollback (no need to clone)
      const originalItem = currItem;

      // Create a list to track all modified child items for potential rollback
      const modifiedChildItems = [];

      const unitFlowFeed = _.get(
        cachedData,
        "node.learningCourseFlowFeedWrapper.learningCourseFlowFeed",
        {}
      );
      if (_.isEmpty(unitFlowFeed)) return;

      const itemIdx = _.findIndex(
        unitFlowFeed.edges,
        ({ node }) => node.id === id
      );

      if (itemIdx === -1) return;

      let updatedFeed = unitFlowFeed;
      const lockingInfo = { isLocked };

      if (updateSelf) {
        const isItemAssessment = isAssessment(currItem);

        if (isItemAssessment) {
          entityType = "ASSESSMENT";
          entityId = updatedItem?.item?.id;

          updatedItem = update(updatedItem, {
            item: {
              lockingInfo: { $set: lockingInfo },
            },
          });
        } else {
          updatedItem = update(updatedItem, {
            lockingInfo: { $set: lockingInfo },
          });
        }

        // Update the lock state of the item in the whole feed
        updatedFeed = update(updatedFeed, {
          edges: {
            [itemIdx]: {
              node: {
                ...(isItemAssessment
                  ? {
                      item: {
                        lockingInfo: { $set: lockingInfo },
                      },
                    }
                  : { lockingInfo: { $set: lockingInfo } }),
              },
            },
          },
        });

        // Update the lock state of the item
        writeUnitFlowItemInCache({
          id,
          variables: feedQueryVariables,
          data: updatedItem,
        });
      }

      const unitFlowFeedNodes = _.map(updatedFeed.edges, "node");

      if (isSection && updateChildren) {
        const nestedChildren = getNestedChildren({
          items: unitFlowFeedNodes,
          id,
        });

        // Update the lock state of children
        _.forEach(nestedChildren, child => {
          const childItem = getUnitFlowItemFromCache({
            id: child.id,
            variables: feedQueryVariables,
          });

          if (!childItem) return;

          const isChildItemConflicted = getHasConflict({ item: childItem });
          if (isChildItemConflicted) hasAnyConflict = true;

          if (!getIsBlueprintSyncHistoryV2Enabled() && isChildItemConflicted) {
            return;
          }

          // Save original child item for potential rollback
          modifiedChildItems.push({
            id: child.id,
            originalData: childItem,
          });

          const childIndex = _.findIndex(
            unitFlowFeed.edges,
            ({ node }) => node.id === child.id
          );

          const isChildItemAssessment = isAssessment(childItem);

          const childNewItem = update(childItem, {
            ...(isChildItemAssessment
              ? {
                  item: {
                    lockingInfo: { $set: lockingInfo },
                  },
                }
              : { lockingInfo: { $set: lockingInfo } }),
          });

          // Update the sharing state of the child item in the whole feed
          updatedFeed = update(updatedFeed, {
            edges: {
              [childIndex]: {
                node: {
                  ...(isChildItemAssessment
                    ? {
                        item: {
                          lockingInfo: { $set: lockingInfo },
                        },
                      }
                    : { lockingInfo: { $set: lockingInfo } }),
                },
              },
            },
          });

          writeUnitFlowItemInCache({
            id: child.id,
            variables: feedQueryVariables,
            data: childNewItem,
          });
        });
      }

      const updatedData = update(cachedData, {
        node: {
          learningCourseFlowFeedWrapper: {
            learningCourseFlowFeed: {
              edges: { $set: updatedFeed.edges },
            },
          },
        },
      });

      // Update the whole feed in cache
      cache.writeQuery({
        query: getEntireUnitFlowFeedQuery,
        variables: feedQueryVariables,
        data: updatedData,
      });

      // Variables to fetch the feed without filters
      // This is used to check if there are any locked items in the feed without any filters applied
      const feedQueryVariablesWithoutFilters = getUnitFlowFeedVariablesMemoized(
        {
          unitPlanId,
          filters: _.get(initialState, "filters", {}),
          userInfo,
          curriculumType,
          learningCourseId,
        }
      );

      const unitFlowFeedWithoutFilters = getEntireUnitFlowFeedQueryFromCache(
        feedQueryVariablesWithoutFilters
      );

      const unitFlowEdgesWithoutFilters = _.get(
        unitFlowFeedWithoutFilters,
        "node.learningCourseFlowFeedWrapper.learningCourseFlowFeed.edges",
        []
      );

      const isUnitFlowLocked =
        !_.isEmpty(unitFlowEdgesWithoutFilters) &&
        _.every(unitFlowEdgesWithoutFilters, ({ node }) =>
          getIsItemLocked({ item: node })
        );

      const centralRepoInput = {
        isSchoolGroup: false,
        region: [],
      };

      const responseFilter = getUnitPlanFieldResponseFilter();

      const collaboratorsFilter = {
        userAcademicYearId: academicYearId,
      };

      const unitPlanDetails = getUnitPlanDetailsFromCache({
        unitPlanId,
        centralRepoInput,
        responseFilter,
        collaboratorsFilter,
        exceptionalCollaboratorsFilter: collaboratorsFilter,
      });

      // Update the unit flow lock state in unit plan details
      const updatedUnitPlanDetails = update(unitPlanDetails, {
        isUnitFlowLocked: { $set: isUnitFlowLocked },
      });

      // Write the updated unit plan details in cache
      writeUnitPlanFragment({
        unitPlanId,
        data: updatedUnitPlanDetails,
        responseFilter,
        centralRepoInput,
        collaboratorsFilter,
        exceptionalCollaboratorsFilter: collaboratorsFilter,
      });

      // [Don't remove] Hack to update the lock state in the UI
      dispatch(setActiveContentData({}));

      try {
        // Now perform the actual mutation
        const variables = {
          input: {
            learningCourseId,
            lockType: "CONTENT_LEVEL_LOCK",
            lockScope,
            entityLocks: [
              {
                entityId,
                entityType,
                isLocked,
              },
            ],
          },
        };

        await client.mutate({
          mutation: updateLockedStateMutation,
          variables,
          optimisticResponse: {
            __typename: "Mutation",
            planner: {
              bulkUpdateLearningCourseEntityLock: {
                isSuccess: true,
              },
              __typename: "PlannerMutations",
            },
          },
        });

        const entityLabelLocale = getItemTypeLocale({
          item: currItem,
          assessmentTypes,
        });

        dispatch(
          setToastMsg({
            msg: getLockingActionToastMsg({
              isLocked,
              updateSelf,
              updateChildren,
              entityLabelLocale,
              hasConflict: hasAnyConflict,
            }),
            notString: true,
            type: "success",
            position: "toast-bottom-left",
          })
        );
      } catch (error) {
        // Revert item cache
        writeUnitFlowItemInCache({
          id,
          variables: feedQueryVariables,
          data: originalItem,
        });

        // Revert all child items
        _.forEach(modifiedChildItems, ({ id, originalData }) => {
          writeUnitFlowItemInCache({
            id,
            variables: feedQueryVariables,
            data: originalData,
          });
        });

        // Revert the feed query cache
        cache.writeQuery({
          query: getEntireUnitFlowFeedQuery,
          variables: feedQueryVariables,
          data: originalCachedData,
        });

        // Revert the unit plan details in cache
        writeUnitPlanFragment({
          unitPlanId,
          data: unitPlanDetails,
          responseFilter,
          centralRepoInput,
          collaboratorsFilter,
          exceptionalCollaboratorsFilter: collaboratorsFilter,
        });

        console.error(error);
        dispatchError({
          dispatch,
          e: error,
          customMessage: t("course:locking_action_error_toast", {
            entityType: t(
              getItemTypeLocale({ item: currItem, assessmentTypes })
            ),
            lockingAction: isLocked ? t("common:locked") : t("common:unlocked"),
          }),
          notString: true,
        });
      }
      return;
    } catch (error) {
      console.error(error);
      dispatchError({
        dispatch,
        e: error,
        customMessage: t("course:locking_action_error_toast", {
          entityType: t("common:resource_label"),
          lockingAction: isLocked ? t("common:locked") : t("common:unlocked"),
        }),
        notString: true,
      });
    }
  };
};

export const fixDepthOfNodes = () => {
  return async (dispatch, getState) => {
    const state = getState();
    const userInfo = getUserInfo({ portalType: "PLANNER" });
    const unitPlanId = _.get(state, "planner.unitPlanData.unitPlanId", "");
    const filters = _.get(state, "unitFlow.filters", {});

    const curriculumType = getCurrentCurriculumProgramType(state);
    const learningCourseId = _.get(
      state,
      "planner.unitPlanData.learningCourseId",
      null
    );

    const feedQueryVariables = getUnitFlowFeedVariablesMemoized({
      unitPlanId,
      filters,
      userInfo,
      curriculumType,
      learningCourseId,
    });

    cache.updateQuery(
      {
        query: getEntireUnitFlowFeedQuery,
        variables: feedQueryVariables,
      },
      data => {
        if (!data) return;
        const unitFlowFeed = _.cloneDeep(
          _.get(
            data,
            `node.${getWrapperFieldName()}.learningCourseFlowFeed`,
            []
          )
        );

        const unitFlowFeedNodes = _.map(unitFlowFeed.edges, "node");

        const learningCourseFlowResource = getLearningCourseFlowResourceFromCache(
          { id: unitPlanId }
        );

        const learningCourseFlowResourceId = _.get(
          learningCourseFlowResource,
          "node.learningCourseFlowResource.id",
          null
        );

        const rootItems = _.filter(unitFlowFeedNodes, [
          "parent.id",
          learningCourseFlowResourceId,
        ]);

        _.forEach(rootItems, rootItem => {
          if (rootItem.depth > 1) {
            updateDepthRecursively({
              items: unitFlowFeed.edges,
              id: rootItem.id,
              factor: -1,
            });
          }
        });

        return update(data, {
          node: {
            [getWrapperFieldName()]: {
              learningCourseFlowFeed: { $set: unitFlowFeed },
            },
          },
        });
      }
    );
  };
};

export const updateUnitFlowItemLockStatusById = ({ resourcesData }) => {
  return async (dispatch, getState) => {
    try {
      const state = getState();
      const academicYearId = getSelectedAcademicYearId(state);
      const learningCourseId = _.get(
        state,
        "planner.unitPlanData.learningCourseId",
        null
      );

      const userInfo = getUserInfo({ portalType: "PLANNER" });
      const curriculumType = _.get(
        state,
        "platform.currentCurriculumProgram.type",
        null
      );

      const unitPlanId = _.get(state, "planner.unitPlanData.unitPlanId", "");
      const filters = _.get(state, "unitFlow.filters", {});
      const feedQueryVariables = getUnitFlowFeedVariablesMemoized({
        unitPlanId,
        filters,
        userInfo,
        curriculumType,
        learningCourseId,
      });

      // Get unit flow feed from cache
      const cachedData = getEntireUnitFlowFeedQueryFromCache(
        feedQueryVariables
      );

      const unitFlowFeedEdges = _.get(
        cachedData,
        "node.learningCourseFlowFeedWrapper.learningCourseFlowFeed.edges",
        []
      );
      if (_.isEmpty(unitFlowFeedEdges)) return;

      let updatedFeedEdges = unitFlowFeedEdges;
      _.forEach(resourcesData, resource => {
        const { entityId, isLocked } = resource;

        const itemIdx = _.findIndex(
          unitFlowFeedEdges,
          ({ node }) =>
            _.get(node, "item.id") === entityId || node.id === entityId
        );

        if (itemIdx === -1) return;

        const currItem = updatedFeedEdges[itemIdx].node;

        const lockingInfo = { isLocked };

        // Update the lock state of the item in the whole feed
        updatedFeedEdges = update(updatedFeedEdges, {
          [itemIdx]: {
            node: {
              ...(_.get(currItem, "item")
                ? {
                    item: {
                      lockingInfo: { $set: lockingInfo },
                    },
                  }
                : {}),
              ...(_.get(currItem, "lockingInfo")
                ? {
                    lockingInfo: { $set: lockingInfo },
                  }
                : {}),
            },
          },
        });
      });

      const updatedData = update(cachedData, {
        node: {
          learningCourseFlowFeedWrapper: {
            learningCourseFlowFeed: {
              edges: { $set: updatedFeedEdges },
            },
          },
        },
      });

      const isUnitFlowLocked =
        !_.isEmpty(updatedFeedEdges) &&
        _.every(updatedFeedEdges, ({ node }) =>
          getIsItemLocked({ item: node })
        );

      const centralRepoInput = {
        isSchoolGroup: false,
        region: [],
      };

      const responseFilter = getUnitPlanFieldResponseFilter();

      const collaboratorsFilter = {
        userAcademicYearId: academicYearId,
      };

      const unitPlanDetails = getUnitPlanDetailsFromCache({
        unitPlanId,
        centralRepoInput,
        responseFilter,
        collaboratorsFilter,
        exceptionalCollaboratorsFilter: collaboratorsFilter,
      });

      // Update the unit flow lock state in unit plan details
      const updatedUnitPlanDetails = update(unitPlanDetails, {
        isUnitFlowLocked: { $set: isUnitFlowLocked },
      });

      // Update the whole feed in cache
      cache.writeQuery({
        query: getEntireUnitFlowFeedQuery,
        variables: feedQueryVariables,
        data: updatedData,
      });

      // Write the updated unit plan details in cache
      writeUnitPlanFragment({
        unitPlanId,
        data: updatedUnitPlanDetails,
        responseFilter,
        centralRepoInput,
        collaboratorsFilter,
        exceptionalCollaboratorsFilter: collaboratorsFilter,
      });
    } catch (e) {
      dispatchError({ dispatch, e });
    }
  };
};
